<template>
	<div class="flex flex-col gap-0.5 bg-white shadow sm:rounded-lg p-4 m-4">
		<div class="flex justify-between w-full gap-2 mb-4">
			<PageLabel :helpPageName="'SystemConfiguration'">PrimeVue Components</PageLabel>
		</div>

		<div class="flex gap-4">
			<!-- Text Formatting Panel -->
			<div class="text-formatting-panel">
				<h3 class="text-formatting-title">Text Formatting</h3>

				<!-- Live Preview -->
				<div class="text-preview-container">
					<div class="text-preview" :style="computedTextStyle" ref="textPreviewRef">Sample Text Preview</div>
				</div>

				<!-- Font Family -->
				<div class="formatting-group">
					<label class="formatting-label">Font Family</label>
					<Select
						v-model="selectedFontFamily"
						:options="fontFamilies"
						optionLabel="label"
						placeholder="Select Font"
						class="w-full"
						@change="onFontFamilyChange"
					/>
				</div>

				<!-- Font Size -->
				<div class="formatting-group">
					<label class="formatting-label">Font Size</label>
					<div class="flex items-center gap-1">
						<InputNumber v-model="textSettings.fontSize" :min="8" :max="72" suffix="px" class="flex-1" @update:modelValue="applyTextFormatting" />
						<div class="font-size-btn-group">
							<Button
								class="p-button-sm font-size-btn font-size-up"
								@click="incrementFontSize"
								:disabled="textSettings.fontSize >= 72"
								severity="secondary"
								aria-label="Increase font size"
							>
								<span class="font-size-arrow">↑</span>
							</Button>
							<Button
								class="p-button-sm font-size-btn font-size-down"
								@click="decrementFontSize"
								:disabled="textSettings.fontSize <= 8"
								severity="secondary"
								aria-label="Decrease font size"
							>
								<span class="font-size-arrow">↓</span>
							</Button>
						</div>
					</div>
				</div>

				<!-- Text Color -->
				<div class="formatting-group">
					<label class="formatting-label">Text Color</label>
					<div class="flex items-center gap-2">
						<div
							class="color-preview"
							:style="{ backgroundColor: textSettings.color }"
							@click="openTextColorPicker($event)"
							data-css-var="text-color"
						></div>
						<InputText v-model="textSettings.color" placeholder="#000000" class="color-input-field" @change="applyTextFormatting" />
					</div>
				</div>

				<!-- Font Weight -->
				<div class="formatting-group">
					<label class="formatting-label">Font Weight</label>
					<Select
						v-model="textSettings.fontWeight"
						:options="fontWeights"
						optionLabel="label"
						optionValue="value"
						placeholder="Select Weight"
						class="w-full"
						@change="applyTextFormatting"
					/>
				</div>

				<!-- Text Decoration -->
				<div class="formatting-group">
					<label class="formatting-label">Text Style</label>
					<div class="text-style-options">
						<ToggleButton v-model="textSettings.bold" onLabel="B" offLabel="B" class="text-style-btn bold-btn" @change="applyTextFormatting" />
						<ToggleButton v-model="textSettings.italic" onLabel="I" offLabel="I" class="text-style-btn italic-btn" @change="applyTextFormatting" />
						<ToggleButton
							v-model="textSettings.underline"
							onLabel="U"
							offLabel="U"
							class="text-style-btn underline-btn"
							@change="applyTextFormatting"
						/>
					</div>
				</div>

				<!-- Line Height -->
				<div class="formatting-group" style="display: none">
					<label class="formatting-label">Line Height</label>
					<InputNumber
						v-model="textSettings.lineHeight"
						:min="1"
						:max="3"
						:step="0.1"
						mode="decimal"
						:minFractionDigits="1"
						:maxFractionDigits="1"
						class="w-full"
						@update:modelValue="applyTextFormatting"
					/>
				</div>

				<!-- Letter Spacing -->
				<div class="formatting-group" style="display: none">
					<label class="formatting-label">Letter Spacing</label>
					<div class="flex items-center gap-2">
						<InputNumber
							v-model="textSettings.letterSpacing"
							:min="-5"
							:max="10"
							:step="0.5"
							mode="decimal"
							suffix="px"
							class="flex-1"
							@update:modelValue="applyTextFormatting"
						/>
					</div>
				</div>

				<!-- Text Transform -->
				<div class="formatting-group">
					<label class="formatting-label">Text Transform</label>
					<Select
						v-model="textSettings.textTransform"
						:options="textTransforms"
						optionLabel="label"
						optionValue="value"
						placeholder="Select Transform"
						class="w-full"
						@change="applyTextFormatting"
					/>
				</div>

				<!-- Apply Button -->
				<div class="formatting-group">
					<Button label="Apply" class="apply-text-btn" @click="applyTextToAllComponents" severity="success" />
				</div>
			</div>

			<div class="components-container">
				<ButtonsPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<InputsPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<SelectionPreview @elementClick="handleClick" :textStyle="computedTextStyle" />

				<NavigationPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<DataTablePreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<DownstreamDistributionPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<LandingPageUtilitiesPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<MessagesPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
				<OverlaysPreview @elementClick="handleClick" :textStyle="computedTextStyle" />
			</div>
		</div>

		<!-- Color Picker Panel -->
		<div v-if="showPicker" :style="{ position: 'fixed', top: pickerY + 'px', left: pickerX + 'px', zIndex: 9999 }" class="color-picker-panel">
			<div class="color-picker-header">
				<span class="color-picker-title">Edit Color</span>
				<button @click="closePicker" class="color-picker-close">×</button>
			</div>
			<div v-if="colorPickerSubtitle" class="color-picker-subtitle">
				{{ colorPickerSubtitle }}
			</div>

			<!-- Mode switching tabs -->
			<div class="mode-tabs">
				<Button @click="switchColorMode('background', $event)" :class="['mode-tab', { active: colorMode === 'background' }]" size="small">
					Background
				</Button>
				<Button @click="switchColorMode('text', $event)" :class="['mode-tab', { active: colorMode === 'text' }]" size="small">
					<i class="pi pi-font"></i> Text
				</Button>
			</div>

			<!-- Auto Contrast Checkbox -->
			<div v-if="colorMode === 'background'" class="auto-contrast-section">
				<div class="flex items-center gap-2 p-2 border-t border-gray-200">
					<PVCheckbox v-model="autoContrast" inputId="autoContrast" binary />
					<label for="autoContrast" class="text-sm text-gray-700 cursor-pointer"> Auto contrast </label>
					<i
						class="pi pi-question-circle text-gray-400 cursor-help"
						title="Automatically adjusts text color (black/white) based on background brightness"
					></i>
				</div>
			</div>

			<div class="color-picker-content">
				<ColorPicker
					v-if="colorMode === 'background'"
					v-model="selectedColor"
					@update:modelValue="onColorPickerChange"
					inline
					ref="colorPickerRef"
					class="color-picker"
				/>
				<ColorPicker v-else v-model="selectedTextColor" @update:modelValue="onColorPickerChange" inline ref="colorPickerRef" class="color-picker" />
				<div class="color-palette-miniatures" v-if="paletteColors">
					<div class="palette-grid">
						<!-- Primary, Secondary, Accent with border -->
						<div class="primary-colors-box">
							<div class="palette-row primary-row">
								<div
									v-for="(color, index) in getPrimaryColors()"
									:key="index"
									class="palette-swatch primary-swatch"
									:style="{ backgroundColor: color.value }"
									@click="selectPaletteColor(color.value)"
									:title="color.name"
								></div>
							</div>
						</div>
						<!-- Other colors -->
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(0, 3)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(3, 6)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(6, 9)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
					</div>
				</div>
			</div>
			<div class="hex-input-container">
				<label for="hexInput" class="hex-input-label">Hex:</label>
				<InputText
					id="hexInput"
					v-model="hexInputValue"
					@input="onHexInputChange"
					@blur="validateAndApplyHex"
					class="hex-input"
					placeholder="#ffffff"
					maxlength="7"
				/>
				<Button @click="sendColorToParent" size="small" class="send-color-btn" title="Use it to generate color scheme">
					<i class="pi pi-arrow-up"></i>
				</Button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, nextTick, watch, onMounted, computed, reactive, onUnmounted, toRaw } from "vue"
import {
	ThemeState,
	ThemeTemplate,
	Severity,
	Chip,
	ColorSchemeChip,
	ChipState,
	ChipRoot,
	Multiselect,
	ColorSchemeMultiselect,
	SelectState,
	Option,
	Checkbox,
	ColorSchemeCheckbox,
	CheckboxState,
	CheckboxRoot,
	Radiobutton,
	ColorSchemeRadiobutton,
	RadiobuttonState,
	RadiobuttonRoot,
	Toggleswitch,
	ColorSchemeToggleswitch,
	ToggleswitchState,
	ToggleswitchRoot,
	ToggleswitchHandle,
	Tabs,
	ColorSchemeTabs,
	TabsLight,
	TabsRoot,
	ActiveBar,
	Tab,
	FocusRing,
	ButtonType,
	Button as ButtonTheme,
	ColorSchemeButton,
	ButtonState,
	ButtonRoot,
} from "../../../entities/NSwagGenerated"
import InputText from "primevue/inputtext"
import ColorPicker from "primevue/colorpicker"
import Select from "primevue/select"
import InputNumber from "primevue/inputnumber"
import ToggleButton from "primevue/togglebutton"
import Button from "primevue/button"
import PVCheckbox from "primevue/checkbox"
import PageLabel from "../pageLabel.vue"
import ButtonsPreview from "./themeComponents/ButtonsPreview.vue"
import InputsPreview from "./themeComponents/InputsPreview.vue"
import SelectionPreview from "./themeComponents/SelectionPreview.vue"

import NavigationPreview from "./themeComponents/NavigationPreview.vue"
import DataTablePreview from "./themeComponents/DataTablePreview.vue"
import { useThemeManagerStore } from "../../store/themeManagerStore"
import MessagesPreview from "./themeComponents/MessagesPreview.vue"
import OverlaysPreview from "./themeComponents/OverlaysPreview.vue"
import DownstreamDistributionPreview from "./themeComponents/DownstreamDistributionPreview.vue"
import LandingPageUtilitiesPreview from "./themeComponents/LandingPageUtilitiesPreview.vue"
import { getOptimalTextColor, lightenColor, isDarkColor } from "../../utils/colorUtils"

const props = defineProps<{
	selectedTemplate?: ThemeState
	selectedColor?: string
	isLoadingTemplate?: boolean
	paletteColors?: Record<string, string>
}>()

const emit = defineEmits<{
	(e: "updateTheme", themeData: ThemeState): void
	(e: "colorSelected", color: string): void
	(e: "disablePasteColor"): void
	(e: "saveToUndo"): void
}>()

// Functions for palette colors
const getPrimaryColors = () => {
	if (!props.paletteColors) return []
	return [
		{ name: "Primary", value: props.paletteColors["0"] },
		{ name: "Secondary", value: props.paletteColors["1"] },
		{ name: "Accent", value: props.paletteColors["2"] },
	]
}

const getOtherColors = () => {
	if (!props.paletteColors) return []
	const colors = []
	for (let i = 3; i < 12; i++) {
		if (props.paletteColors[i.toString()]) {
			colors.push({ name: "", value: props.paletteColors[i.toString()] })
		}
	}
	return colors
}

const selectPaletteColor = (color: string) => {
	// Set the appropriate color based on current mode
	if (colorMode.value === "text") {
		selectedTextColor.value = color
	} else {
		selectedColor.value = color

		// Auto-contrast: automatically set optimal text color if enabled
		if (autoContrast.value && clickedElement) {
			const optimalTextColor = getOptimalTextColor(color)

			// Save current mode and switch to text mode temporarily
			const originalMode = colorMode.value
			colorMode.value = "text"

			// Set optimal text color
			selectedTextColor.value = optimalTextColor
			hexInputValue.value = optimalTextColor

			// Apply the color to the current component (as if user clicked on text)
			applyColor()

			// Restore original mode
			colorMode.value = originalMode
		}
	}

	hexInputValue.value = color.toUpperCase()
	applyColor()
}

// Theme state (following login page pattern)
const localThemeState = ref<ThemeState>(new ThemeState())

// Color picker state
const showPicker = ref(false)
const selectedColor = ref("#ffffff")
const selectedTextColor = ref("#374151")
const hexInputValue = ref("#ffffff")
const pickerX = ref(0)
const pickerY = ref(0)
const colorPickerRef = ref()
const currentColorType = ref<"text" | "component">("component")
const colorMode = ref<"background" | "text">("background")
const autoContrast = ref<boolean>(true) // Auto-contrast enabled by default
let clickedElement: HTMLElement | null = null

// ToggleSwitch state tracking
const toggleswitchState = ref<"checked" | "unchecked" | null>(null)
const colorPickerSubtitle = ref<string>("")

// Theme Manager Store
const themeStore = useThemeManagerStore()

// Font settings category for saving
const fontSettingCategory = ref("General")
// Keep track of current font setting ID for updates
const currentFontSettingId = ref<string | undefined>(undefined)

// Text formatting settings
const textSettings = reactive({
	fontFamily: "Arial, sans-serif",
	fontSize: 14,
	color: "#374151",
	fontWeight: "400",
	bold: false,
	italic: false,
	underline: false,
	lineHeight: 1.5,
	letterSpacing: 0,
	textTransform: "none",
	fontStyle: "normal",
	textDecoration: "none",
})

// Font family options
const fontFamilies = [
	{ label: "Arial", value: "Arial, sans-serif" },
	{ label: "Times New Roman", value: "'Times New Roman', serif" },
	{ label: "Georgia", value: "Georgia, serif" },
	{ label: "Courier New", value: "'Courier New', monospace" },
	{ label: "Helvetica", value: "Helvetica, sans-serif" },
	{ label: "Verdana", value: "Verdana, sans-serif" },
	{ label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
	{ label: "Comic Sans", value: "'Comic Sans MS', cursive" },
	{ label: "Impact", value: "Impact, sans-serif" },
	{ label: "Inter (fallback)", value: "Inter, Arial, sans-serif" },
]

// Selected font family object for dropdown
const selectedFontFamily = ref(fontFamilies[0])

// Ref for text preview element
const textPreviewRef = ref<HTMLElement>()

// Font weight options
const fontWeights = [
	{ label: "Thin", value: "100" },
	{ label: "Extra Light", value: "200" },
	{ label: "Light", value: "300" },
	{ label: "Regular", value: "400" },
	{ label: "Medium", value: "500" },
	{ label: "Semi Bold", value: "600" },
	{ label: "Bold", value: "700" },
	{ label: "Extra Bold", value: "800" },
	{ label: "Black", value: "900" },
]

// Text transform options
const textTransforms = [
	{ label: "None", value: "none" },
	{ label: "Uppercase", value: "uppercase" },
	{ label: "Lowercase", value: "lowercase" },
	{ label: "Capitalize", value: "capitalize" },
]

// Computed property for text style
const computedTextStyle = computed(() => {
	return {
		fontFamily: textSettings.fontFamily,
		fontSize: `${textSettings.fontSize}px`,
		color: textSettings.color,
		fontWeight: textSettings.bold ? "700" : textSettings.fontWeight,
		fontStyle: textSettings.italic ? "italic" : "normal",
		textDecoration: textSettings.underline ? "underline" : "none",
		lineHeight: textSettings.lineHeight,
		letterSpacing: `${textSettings.letterSpacing}px`,
		textTransform: textSettings.textTransform,
	}
})

// Handle font family change
const onFontFamilyChange = () => {
	if (selectedFontFamily.value) {
		textSettings.fontFamily = selectedFontFamily.value.value
		applyTextFormatting()
	}
}

// Apply text formatting to components
const applyTextFormatting = () => {
	// Apply the formatting to all preview components
	applyTextToAllComponents()
}

// Handle color picker change
const onColorPickerChange = (value: string) => {
	if (colorMode.value === "background") {
		selectedColor.value = value
		// Update hex input to match
		if (value && value.startsWith("#")) {
			hexInputValue.value = value.toUpperCase()
		} else if (value) {
			hexInputValue.value = "#" + value.toUpperCase()
		}

		// Auto-contrast: automatically set optimal text color if enabled
		if (autoContrast.value && value && clickedElement) {
			const backgroundColor = value.startsWith("#") ? value : "#" + value
			const optimalTextColor = getOptimalTextColor(backgroundColor)

			// Save current mode and switch to text mode temporarily
			const originalMode = colorMode.value
			colorMode.value = "text"

			// Set optimal text color
			selectedTextColor.value = optimalTextColor
			hexInputValue.value = optimalTextColor

			// Apply the color to the current component (as if user clicked on text)
			applyColor()

			// Restore original mode
			colorMode.value = originalMode
		}
	} else {
		// Text color mode
		selectedTextColor.value = value
		// Update hex input for text color too
		if (value && value.startsWith("#")) {
			hexInputValue.value = value.toUpperCase()
		} else if (value) {
			hexInputValue.value = "#" + value.toUpperCase()
		}
	}

	applyColor()
}

// Watch selectedColor to update hexInputValue
watch(selectedColor, (newColor) => {
	if (colorMode.value === "background" && newColor) {
		if (newColor.startsWith("#")) {
			hexInputValue.value = newColor.toUpperCase()
		} else {
			hexInputValue.value = "#" + newColor.toUpperCase()
		}
	}
})

// Watch selectedTextColor to update hexInputValue
watch(selectedTextColor, (newColor) => {
	if (colorMode.value === "text" && newColor) {
		if (newColor.startsWith("#")) {
			hexInputValue.value = newColor.toUpperCase()
		} else {
			hexInputValue.value = "#" + newColor.toUpperCase()
		}
	}
})

// Watch colorMode to update hexInputValue when switching modes
watch(colorMode, (newMode) => {
	const currentColor = newMode === "background" ? selectedColor.value : selectedTextColor.value
	if (currentColor) {
		if (currentColor.startsWith("#")) {
			hexInputValue.value = currentColor.toUpperCase()
		} else {
			hexInputValue.value = "#" + currentColor.toUpperCase()
		}
	}
})

// Debug watcher for showPicker
watch(showPicker, (newValue, oldValue) => {
	if (!newValue && oldValue) {
		console.trace("🎨 Picker was closed - trace:")
	}
})

// Watchers to keep computed values in sync with toggle buttons
watch(
	() => textSettings.bold,
	(newVal) => {
		textSettings.fontWeight = newVal ? "700" : "400"
	}
)

watch(
	() => textSettings.italic,
	(newVal) => {
		textSettings.fontStyle = newVal ? "italic" : "normal"
	}
)

watch(
	() => textSettings.underline,
	(newVal) => {
		textSettings.textDecoration = newVal ? "underline" : "none"
	}
)

// Watcher to force apply styles to preview element
watch(
	[() => textSettings.fontFamily, () => textSettings.fontSize, () => textSettings.color, () => textSettings.fontWeight],
	() => {
		if (textPreviewRef.value) {
			const element = textPreviewRef.value
			// Force apply styles directly to DOM element
			element.style.fontFamily = textSettings.fontFamily
			element.style.fontSize = `${textSettings.fontSize}px`
			element.style.color = textSettings.color
			element.style.fontWeight = textSettings.fontWeight
			element.style.fontStyle = textSettings.italic ? "italic" : "normal"
			element.style.textDecoration = textSettings.underline ? "underline" : "none"
			element.style.lineHeight = textSettings.lineHeight.toString()
			element.style.letterSpacing = `${textSettings.letterSpacing}px`
		}
	},
	{ immediate: true }
)

// Font size increment/decrement functions
const incrementFontSize = () => {
	if (textSettings.fontSize < 72) {
		textSettings.fontSize = Math.min(textSettings.fontSize + 2, 72)
		applyTextFormatting()
	}
}

const decrementFontSize = () => {
	if (textSettings.fontSize > 8) {
		textSettings.fontSize = Math.max(textSettings.fontSize - 2, 8)
		applyTextFormatting()
	}
}

// Apply text formatting to all components in preview
const applyTextToAllComponents = () => {
	// Force refresh the preview components by applying styles directly
	const container = document.querySelector(".components-container")
	if (!container) return

	// Select all text elements that should be styled (excluding buttons)
	const textElements = container.querySelectorAll(`
			label,
			.p-inputtext,
			.p-dropdown-label,
			.p-multiselect-label,
			.p-checkbox-label,
			.p-radiobutton-label,
			.p-tabmenu-nav a,
			.p-breadcrumb a,
			.p-steps-title,
			.p-datatable th,
			.p-datatable td,
			.p-message-text,
			.p-toast-message-text,
			.p-dialog-title,
			.p-dialog-content,
			.p-card-title,
			.p-card-subtitle,
			.p-card-content,
			.p-tag,
			.p-chip,
			.p-accordion-header-text,
			span:not(.p-button-label):not(.p-button-icon):not(.text-preview *),
			p:not(.text-preview),
			h1:not(.text-preview *), h2:not(.text-preview *), h3:not(.text-preview *),
			h4:not(.text-preview *), h5:not(.text-preview *), h6:not(.text-preview *)
		`)

	textElements.forEach((element: Element) => {
		const el = element as HTMLElement

		// Skip DataTable elements if DataTable has saved colors
		const isDataTableElement = el.closest(".p-datatable")
		const hasDataTableColors =
			localThemeState.value.selectedTheme?.components?.datatable?.colorScheme?.light?.headerCell?.color ||
			localThemeState.value.selectedTheme?.components?.datatable?.colorScheme?.light?.bodyCell?.color

		// Skip Button elements if Button has saved colors
		const isButtonElement = el.closest(".p-button")
		const hasButtonColors =
			localThemeState.value.selectedTheme?.components?.button?.colorScheme?.light?.root &&
			Object.keys(localThemeState.value.selectedTheme.components.button.colorScheme.light.root).length > 0

		if ((isDataTableElement && hasDataTableColors) || (isButtonElement && hasButtonColors)) {
			// Apply only non-color styles to DataTable/Button elements with saved colors
			el.style.fontFamily = textSettings.fontFamily
			el.style.fontSize = `${textSettings.fontSize}px`
			el.style.fontWeight = textSettings.fontWeight
			el.style.fontStyle = textSettings.fontStyle
			el.style.textDecoration = textSettings.textDecoration
			el.style.lineHeight = textSettings.lineHeight.toString()
			el.style.letterSpacing = `${textSettings.letterSpacing}px`
			el.style.textTransform = textSettings.textTransform as any
			// Skip color for elements with saved colors
		} else {
			// Apply all styles including color to other elements
			el.style.fontFamily = textSettings.fontFamily
			el.style.fontSize = `${textSettings.fontSize}px`
			el.style.color = textSettings.color
			el.style.fontWeight = textSettings.fontWeight
			el.style.fontStyle = textSettings.fontStyle
			el.style.textDecoration = textSettings.textDecoration
			el.style.lineHeight = textSettings.lineHeight.toString()
			el.style.letterSpacing = `${textSettings.letterSpacing}px`
			el.style.textTransform = textSettings.textTransform as any
		}
	})
}

// Local interface to replace the removed AppSettings_fontSettings
interface FontSettings {
	id: string
	themeId?: string
	fontFamily?: string
	fontSize?: string | number
	fontWeight?: string
	letterSpacing?: string | number
	lineHeight?: string | number
	textDecoration?: string
	textTransform?: string
	color?: string
	isDefault?: boolean
	[key: string]: any
}

// Function to get current font settings for saving (used by theme template save)
const getCurrentFontSettings = (): FontSettings => {
	return {
		id: currentFontSettingId.value || "00000000-0000-0000-0000-000000000000", // Use current ID for updates or empty for new
		themeId: themeStore.templateUI?.id || "",
		fontFamily: textSettings.fontFamily,
		fontSize: textSettings.fontSize, // Should be number, not string
		fontWeight: textSettings.fontWeight, // Now updated by watcher
		lineHeight: textSettings.lineHeight, // Should be number, not string
		letterSpacing: textSettings.letterSpacing, // Should be number, not string
		fontStyle: textSettings.fontStyle, // Now updated by watcher
		textDecoration: textSettings.textDecoration, // Now updated by watcher
		textTransform: textSettings.textTransform,
		color: textSettings.color || "#000000",
		isDefault: true, // Use isDefault instead of isActive, as per C# entity
	}
}

// Migration function - no longer works as database access has been removed
// Themes without font settings in JSON will use defaults
const migrateFontSettingsFromDatabase = async () => {
	// Use default font settings since we can't access the database anymore
	currentFontSettingId.value = undefined
	textSettings.fontFamily = "Inter, sans-serif"
	selectedFontFamily.value = fontFamilies[0]
	textSettings.color = "#000000"
	textSettings.fontSize = 14
	textSettings.fontWeight = "400"
	textSettings.lineHeight = 1.5
	textSettings.letterSpacing = 0
	textSettings.fontStyle = "normal"
	textSettings.textDecoration = "none"
	textSettings.textTransform = "none"

	// Update toggle states
	textSettings.bold = false
	textSettings.italic = false
	textSettings.underline = false

	// Apply the default font settings to preview
	applyTextFormatting()
	applyTextToAllComponents()

	// Re-apply component-specific colors after text formatting (to avoid overrides)
	if (localThemeState.value.selectedTheme?.components?.button) {
		applyButtonColors(localThemeState.value.selectedTheme.components.button)
	}
	if (localThemeState.value.selectedTheme?.components?.datatable) {
		applyDatatableColors(localThemeState.value.selectedTheme.components.datatable)
	}
}

// Load font settings from theme JSON
const loadFontSettingsFromTheme = async () => {
	// Use localThemeState if in preview mode, otherwise use themeStore
	const templateUI = localThemeState.value?.templateUI || themeStore.templateUI
	const selectedTheme = localThemeState.value?.selectedTheme || themeStore.selectedTheme

	// Check if fontSettings exist at ThemeTemplate level (not in JSON)
	if ((selectedTheme as any)?.fontSettings) {
		const fontSettings = (selectedTheme as any).fontSettings

		// Apply these settings
		currentFontSettingId.value = fontSettings.id
		textSettings.fontFamily = fontSettings.fontFamily || "Inter, sans-serif"
		selectedFontFamily.value = fontFamilies.find((f) => f.value === textSettings.fontFamily) || fontFamilies[0]
		textSettings.color = fontSettings.color || "#000000"
		textSettings.fontSize =
			typeof fontSettings.fontSize === "number"
				? fontSettings.fontSize
				: parseInt((fontSettings.fontSize as any)?.toString().replace("px", "") || "14")
		textSettings.fontWeight = fontSettings.fontWeight || "400"
		textSettings.lineHeight =
			typeof fontSettings.lineHeight === "number" ? fontSettings.lineHeight : parseFloat((fontSettings.lineHeight as any)?.toString() || "1.5")
		textSettings.letterSpacing =
			typeof fontSettings.letterSpacing === "number"
				? fontSettings.letterSpacing
				: parseFloat(((fontSettings.letterSpacing as any)?.toString() || "0").replace("px", ""))
		textSettings.fontStyle = fontSettings.fontStyle || "normal"
		textSettings.textDecoration = fontSettings.textDecoration || "none"
		textSettings.textTransform = fontSettings.textTransform || "none"

		// Update toggle states
		textSettings.bold = fontSettings.fontWeight === "700" || fontSettings.fontWeight === "bold"
		textSettings.italic = fontSettings.fontStyle === "italic"
		textSettings.underline = fontSettings.textDecoration === "underline"

		// Apply the font settings to preview
		applyTextFormatting()
		applyTextToAllComponents()

		return
	}

	if (templateUI?.themeSettings) {
		try {
			// Parse themeSettings JSON to get font settings

			const themeSettings = JSON.parse(templateUI.themeSettings)

			const fontSettings = themeSettings.fontSettings

			if (fontSettings) {
				// Clear database ID since we're using JSON now
				currentFontSettingId.value = undefined

				// Parse and apply the font settings to the form
				textSettings.fontFamily = fontSettings.fontFamily || "Inter, sans-serif"
				// Update the dropdown selection
				selectedFontFamily.value = fontFamilies.find((f) => f.value === textSettings.fontFamily) || fontFamilies[0]
				textSettings.color = fontSettings.color || "#000000"
				textSettings.fontSize =
					typeof fontSettings.fontSize === "number" ? fontSettings.fontSize : parseInt(fontSettings.fontSize?.toString().replace("px", "") || "14")
				textSettings.fontWeight = fontSettings.fontWeight || "400"
				textSettings.lineHeight =
					typeof fontSettings.lineHeight === "number" ? fontSettings.lineHeight : parseFloat(fontSettings.lineHeight?.toString() || "1.5")
				textSettings.letterSpacing =
					typeof fontSettings.letterSpacing === "number"
						? fontSettings.letterSpacing
						: parseFloat((fontSettings.letterSpacing?.toString() || "0").replace("px", ""))
				textSettings.fontStyle = fontSettings.fontStyle || "normal"
				textSettings.textDecoration = fontSettings.textDecoration || "none"
				textSettings.textTransform = fontSettings.textTransform || "none"

				// Update toggle states based on loaded values
				textSettings.bold = fontSettings.fontWeight === "700" || fontSettings.fontWeight === "bold"
				textSettings.italic = fontSettings.fontStyle === "italic"
				textSettings.underline = fontSettings.textDecoration === "underline"

				// Apply the loaded font settings to preview only (not globally during editing)
				applyTextFormatting()
				// Also apply directly to preview components (like pressing Apply button)
				applyTextToAllComponents()

				// Re-apply component-specific colors after text formatting (to avoid overrides)
				if (localThemeState.value.selectedTheme?.components?.button) {
					applyButtonColors(localThemeState.value.selectedTheme.components.button)
				}
				if (localThemeState.value.selectedTheme?.components?.datatable) {
					applyDatatableColors(localThemeState.value.selectedTheme.components.datatable)
				}
			} else {
				// No font settings found in JSON, try to migrate from database
				await migrateFontSettingsFromDatabase()
			}
		} catch (error) {
			console.error("❌ Error loading font settings from JSON:", error)
			// Reset ID on error and set default black color
			currentFontSettingId.value = undefined
			textSettings.color = "#000000" // Set black color as fallback
		}
	} else {
		// No themeSettings JSON available, use defaults
		currentFontSettingId.value = undefined
		textSettings.color = "#000000"
	}
}

// Open text color picker
const openTextColorPicker = (event: MouseEvent) => {
	event.preventDefault()
	event.stopPropagation()

	// Remove any existing click outside listener
	document.removeEventListener("click", handleClickOutside)

	// Mark this as a text color picker BEFORE opening
	currentColorType.value = "text"
	clickedElement = { dataset: { cssVar: "text-color" } } as any

	// Set current text color
	selectedColor.value = textSettings.color
	hexInputValue.value = textSettings.color

	// Position the color picker relative to the clicked element
	const target = event.target as HTMLElement
	const rect = target.getBoundingClientRect()

	// Position to the right of the color preview with more distance
	pickerX.value = rect.right + 50
	pickerY.value = rect.top - 20

	// Open the picker
	showPicker.value = true

	// Use setTimeout with 0 delay to ensure the current event propagation completes
	setTimeout(() => {
		document.addEventListener("click", handleClickOutside)
	}, 0)
}

// Component is ready for preview-only color modifications

onMounted(async () => {
	// Initialize with default theme if available
	await nextTick()
	// Initialize downstream distribution defaults on mount
	await initializeDownstreamDistributionDefaults()
	// Load font settings from current theme
	await loadFontSettingsFromTheme()
})

// Watch for theme changes to reload font settings
watch(
	() => [themeStore.templateUI?.id, localThemeState.value?.templateUI?.id],
	async ([storeThemeId, localThemeId]) => {
		const activeThemeId = localThemeId || storeThemeId
		if (activeThemeId) {
			await loadFontSettingsFromTheme()
		}
	}
)

// Apply theme template (following login page pattern exactly)
const applyTemplate = async (selectedTemplate: ThemeState) => {
	// Initialize theme structure if needed
	if (!selectedTemplate.selectedTheme) {
		selectedTemplate.selectedTheme = new ThemeTemplate()
	}

	// Ensure semantic structure exists
	if (!selectedTemplate.selectedTheme.semantic) {
		const semanticObj = {
			init: (_data?: any) => {},
			toJSON: () => ({}),
		} as any
		selectedTemplate.selectedTheme.semantic = semanticObj
	}

	// Cast to any to work with dynamic structure
	const semantic = selectedTemplate.selectedTheme.semantic as any

	// Initialize severity structure if not present or if it's not a proper Severity instance
	if (!semantic.severity || typeof semantic.severity.toJSON !== "function") {
		// Create a proper Severity instance
		const severityInstance = new Severity()
		severityInstance.primary = semantic.severity?.primary || "#6366F1"
		severityInstance.secondary = semantic.severity?.secondary || "#64748B"
		severityInstance.success = semantic.severity?.success || "#22C55E"
		severityInstance.info = semantic.severity?.info || "#06B6D4"
		severityInstance.warning = semantic.severity?.warning || "#F97316"
		severityInstance.danger = semantic.severity?.danger || "#EF4444"

		semantic.severity = severityInstance
	}

	// Initialize components structure directly on selectedTheme if needed
	if (!selectedTemplate.selectedTheme.components) {
		selectedTemplate.selectedTheme.components = {} as any
	}

	// Initialize chip structure if not present or not a proper instance
	if (!selectedTemplate.selectedTheme.components.chip || typeof selectedTemplate.selectedTheme.components.chip.toJSON !== "function") {
		const existingChip = selectedTemplate.selectedTheme.components.chip
		const chipInstance = new Chip()
		const colorScheme = new ColorSchemeChip()
		const lightState = new ChipState()
		const chipRoot = new ChipRoot()

		// Preserve existing chip colors if available, otherwise use defaults
		const defaultBackground = "#e5e7eb"
		chipRoot.background = existingChip?.colorScheme?.light?.root?.background || defaultBackground

		// If no existing colors, calculate them automatically based on background
		if (!existingChip?.colorScheme?.light?.root?.color) {
			const bgColor = chipRoot.background
			const isLight = isColorLight(bgColor)
			const textColor = isLight ? "#000000" : "#ffffff"
			chipRoot.color = textColor
			chipRoot.iconColor = textColor

			// Calculate hover colors
			chipRoot.hoverBackground = calculateHoverColor(bgColor, -0.1)
			const isHoverLight = isColorLight(chipRoot.hoverBackground)
			chipRoot.hoverColor = isHoverLight ? "#000000" : "#ffffff"
		} else {
			// Preserve existing values
			chipRoot.color = existingChip.colorScheme.light.root.color
			chipRoot.iconColor = existingChip.colorScheme.light.root.iconColor || existingChip.colorScheme.light.root.color
			chipRoot.hoverBackground = existingChip.colorScheme.light.root.hoverBackground || calculateHoverColor(chipRoot.background, -0.1)
			chipRoot.hoverColor = existingChip.colorScheme.light.root.hoverColor || chipRoot.color
		}

		lightState.root = chipRoot
		colorScheme.light = lightState
		chipInstance.colorScheme = colorScheme
		selectedTemplate.selectedTheme.components.chip = chipInstance
	}

	// Initialize multiselect structure if not present or not a proper instance
	if (!selectedTemplate.selectedTheme.components.multiselect || typeof selectedTemplate.selectedTheme.components.multiselect.toJSON !== "function") {
		const multiselectInstance = new Multiselect()
		const colorScheme = new ColorSchemeMultiselect()
		// Multiselect uses SelectState which should already be available
		// We'll create the structure dynamically in the color mapping function
		multiselectInstance.colorScheme = colorScheme
		selectedTemplate.selectedTheme.components.multiselect = multiselectInstance
	}

	// Initialize checkbox structure if not present or not a proper instance
	if (!selectedTemplate.selectedTheme.components.checkbox || typeof selectedTemplate.selectedTheme.components.checkbox.toJSON !== "function") {
		const checkboxInstance = new Checkbox()
		const colorScheme = new ColorSchemeCheckbox()
		const lightState = new CheckboxState()
		const checkboxRoot = new CheckboxRoot()

		// Set default checkbox colors or preserve existing ones
		checkboxRoot.checkedBackground = selectedTemplate.selectedTheme.components.checkbox?.colorScheme?.light?.root?.checkedBackground || "#6366F1"
		checkboxRoot.background = selectedTemplate.selectedTheme.components.checkbox?.colorScheme?.light?.root?.background || "#ffffff"
		checkboxRoot.hoverBackground = selectedTemplate.selectedTheme.components.checkbox?.colorScheme?.light?.root?.hoverBackground || "#f3f4f6"

		lightState.root = checkboxRoot
		colorScheme.light = lightState
		checkboxInstance.colorScheme = colorScheme
		selectedTemplate.selectedTheme.components.checkbox = checkboxInstance
	}

	// Initialize radiobutton structure if not present or not a proper instance
	if (!selectedTemplate.selectedTheme.components.radiobutton || typeof selectedTemplate.selectedTheme.components.radiobutton.toJSON !== "function") {
		const radiobuttonInstance = new Radiobutton()
		const colorScheme = new ColorSchemeRadiobutton()
		const lightState = new RadiobuttonState()
		const radiobuttonRoot = new RadiobuttonRoot()

		// Set default radiobutton colors or preserve existing ones
		radiobuttonRoot.checkedBackground =
			selectedTemplate.selectedTheme.components.radiobutton?.colorScheme?.light?.root?.checkedBackground || "#6366F1"
		radiobuttonRoot.background = selectedTemplate.selectedTheme.components.radiobutton?.colorScheme?.light?.root?.background || "#ffffff"
		radiobuttonRoot.hoverBackground = selectedTemplate.selectedTheme.components.radiobutton?.colorScheme?.light?.root?.hoverBackground || "#f3f4f6"
		radiobuttonRoot.borderColor = selectedTemplate.selectedTheme.components.radiobutton?.colorScheme?.light?.root?.borderColor || "#d1d5db"
		radiobuttonRoot.checkedBorderColor =
			selectedTemplate.selectedTheme.components.radiobutton?.colorScheme?.light?.root?.checkedBorderColor || "#6366F1"

		lightState.root = radiobuttonRoot
		colorScheme.light = lightState
		radiobuttonInstance.colorScheme = colorScheme
		selectedTemplate.selectedTheme.components.radiobutton = radiobuttonInstance
	}

	// Initialize toggleswitch structure if not present or not a proper instance
	if (
		!selectedTemplate.selectedTheme.components.toggleswitch ||
		typeof selectedTemplate.selectedTheme.components.toggleswitch.toJSON !== "function"
	) {
		const toggleswitchInstance = new Toggleswitch()
		const colorScheme = new ColorSchemeToggleswitch()
		const lightState = new ToggleswitchState()
		const toggleswitchRoot = new ToggleswitchRoot()
		const handle = new ToggleswitchHandle()

		// Set default toggleswitch colors or preserve existing ones
		toggleswitchRoot.checkedBackground =
			selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.checkedBackground || "#6366F1"
		// Calculate checkedHoverBackground as a lighter version of checkedBackground
		const checkedBg = toggleswitchRoot.checkedBackground
		toggleswitchRoot.checkedHoverBackground =
			selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.checkedHoverBackground || lightenColor(checkedBg, 15)
		toggleswitchRoot.background = selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.background || "#d1d5db"
		toggleswitchRoot.hoverBackground = selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.hoverBackground || "#9ca3af"
		toggleswitchRoot.borderColor = selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.borderColor || "#d1d5db"
		toggleswitchRoot.checkedBorderColor =
			selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.checkedBorderColor || "#6366F1"

		// Set default handle colors
		handle.background = selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.handle?.background || "#ffffff"
		handle.hoverBackground = selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.handle?.hoverBackground || "#f9fafb"
		handle.checkedBackground =
			selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.handle?.checkedBackground || "#ffffff"
		handle.checkedHoverBackground =
			selectedTemplate.selectedTheme.components.toggleswitch?.colorScheme?.light?.root?.handle?.checkedHoverBackground || "#f9fafb"

		toggleswitchRoot.handle = handle
		lightState.root = toggleswitchRoot
		colorScheme.light = lightState
		toggleswitchInstance.colorScheme = colorScheme
		selectedTemplate.selectedTheme.components.toggleswitch = toggleswitchInstance
	}

	// Initialize dialog structure in semantic if not present
	if (!semantic.dialog) {
		semantic.dialog = {
			titleBackgroundColor: "#084c61",
			titleTextColor: "#ffffff",
		}
	}

	// Initialize formColors structure in semantic if not present
	if (!semantic.formColors) {
		semantic.formColors = {
			errorColor: "#e24c4c",
			labelColor: "#374151",
			borderColor: "#d1d5db",
			focusBorderColor: "#8b5cf6",
			warningIconColor: "#f9a800",
		}
	}

	// Initialize datatable components structure if not present
	if (!selectedTemplate.selectedTheme.components.datatable) {
		selectedTemplate.selectedTheme.components.datatable = {
			colorScheme: {
				light: {
					headerCell: { background: "#f8f9fa", color: "#495057", borderColor: "#dee2e6" },
					bodyCell: { background: "#ffffff", color: "#495057", borderColor: "#dee2e6" },
					row: { hoverBackground: "#f8f9fa", selectedBackground: "#e3f2fd" },
				},
			},
		} as any
	}

	// Initialize toast components structure if not present
	if (!selectedTemplate.selectedTheme.components.toast) {
		selectedTemplate.selectedTheme.components.toast = {
			colorScheme: {
				light: {
					root: {
						success: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
						error: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
						warning: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
						info: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
					},
				},
			},
		} as any
	}

	// Copy the template to local state
	localThemeState.value = selectedTemplate

	// Wait for next tick to ensure reactivity
	await nextTick()

	// Reset all inline styles applied by the color picker
	const elementsWithInlineStyles = document.querySelectorAll('[style*="background-color"], [style*="color"], [style*="border-color"]')
	elementsWithInlineStyles.forEach((element) => {
		const htmlElement = element as HTMLElement
		htmlElement.style.removeProperty("background-color")
		htmlElement.style.removeProperty("color")
		htmlElement.style.removeProperty("border-color")
	})

	// Apply saved component colors if they exist
	await applyComponentColors()

	// Initialize downstream distribution table with default colors if not already set
	await initializeDownstreamDistributionDefaults()
}

// Apply saved component colors from theme
const applyComponentColors = async () => {
	if (!localThemeState.value.selectedTheme?.semantic) return

	const semantic = localThemeState.value.selectedTheme.semantic as any

	// Apply button severity colors if saved
	if (semantic.severity) {
		applyButtonSeverityColors(semantic.severity)
	}

	// Apply button colors if saved from components (not semantic.components)
	if (localThemeState.value.selectedTheme?.components?.button) {
		applyButtonColors(localThemeState.value.selectedTheme.components.button)
	}

	// Apply chip colors if saved from components (not semantic.components)
	if (localThemeState.value.selectedTheme?.components?.chip) {
		applyChipColors(localThemeState.value.selectedTheme.components.chip)
	}

	// Apply multiselect colors if saved from components (not semantic.components)
	if (localThemeState.value.selectedTheme?.components?.multiselect) {
		applyMultiselectColors(localThemeState.value.selectedTheme.components.multiselect)
	}

	// Apply checkbox colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.checkbox) {
		applyCheckboxColors(localThemeState.value.selectedTheme.components.checkbox)
	}

	// Apply radiobutton colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.radiobutton) {
		applyRadiobuttonColors(localThemeState.value.selectedTheme.components.radiobutton)
	}

	// Apply toggleswitch colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.toggleswitch) {
		applyToggleswitchColors(localThemeState.value.selectedTheme.components.toggleswitch)
	}

	// Apply tabs colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.tabs) {
		applyTabsColors(localThemeState.value.selectedTheme.components.tabs)
	}

	// Apply message colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.message) {
		applyMessageColors(localThemeState.value.selectedTheme.components.message)
	}

	// Apply toast colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.toast) {
		applyToastColors(localThemeState.value.selectedTheme.components.toast)
	}

	// Apply dialog colors if saved from semantic
	if (semantic.dialog) {
		applyDialogColors(semantic.dialog)
	}

	// Apply form field colors if saved from semantic
	if (semantic.formColors) {
		applyFormColors(semantic.formColors)
	}

	// Apply progressbar colors if saved from components
	if (localThemeState.value.selectedTheme?.components?.progressbar) {
		applyProgressbarColors(localThemeState.value.selectedTheme.components.progressbar)
	}

	// Apply datatable colors if saved
	if (localThemeState.value.selectedTheme?.components?.datatable) {
		applyDatatableColors(localThemeState.value.selectedTheme.components.datatable)
	}

	// Apply downstream distribution table colors if saved
	if (semantic.downstreamDistributionTable) {
		applyDownstreamDistributionColors(semantic.downstreamDistributionTable)
	} else {
	}

	// Apply landing page utilities buttons colors if saved
	if (semantic.landingpageUtilitiesButtons) {
		applyLandingPageUtilitiesColors(semantic.landingpageUtilitiesButtons)
	}

	// Apply other component colors...
}

// Helper function to apply button severity colors from semantic.severity
const applyButtonSeverityColors = (severityConfig: any) => {
	if (!severityConfig) return

	// Map severity names to button severity classes
	const severityMapping: { [key: string]: string } = {
		primary: "p-button-primary",
		secondary: "p-button-secondary",
		success: "p-button-success",
		info: "p-button-info",
		warning: "p-button-warn",
		danger: "p-button-danger",
	}

	// Apply colors to each severity type
	Object.entries(severityConfig).forEach(([severityType, color]) => {
		if (typeof color === "string" && severityMapping[severityType]) {
			// Find buttons with this severity
			const buttons = document.querySelectorAll(`.${severityMapping[severityType]}:not(.p-button-outlined):not(.p-button-text)`)
			buttons.forEach((button: Element) => {
				const htmlButton = button as HTMLElement
				htmlButton.style.backgroundColor = color
				htmlButton.style.borderColor = color
				// Don't automatically calculate text color - let it be set manually
			})

			// Also apply to outlined buttons
			const outlinedButtons = document.querySelectorAll(`.${severityMapping[severityType]}.p-button-outlined`)
			outlinedButtons.forEach((button: Element) => {
				const htmlButton = button as HTMLElement
				htmlButton.style.color = color
				htmlButton.style.borderColor = color
				htmlButton.style.backgroundColor = "transparent"
			})

			// Also apply to text buttons
			const textButtons = document.querySelectorAll(`.${severityMapping[severityType]}.p-button-text`)
			textButtons.forEach((button: Element) => {
				const htmlButton = button as HTMLElement
				htmlButton.style.color = color
				htmlButton.style.backgroundColor = "transparent"
				htmlButton.style.borderColor = "transparent"
			})
		}
	})
}

// Helper functions to apply saved colors
const applyButtonColors = (buttonConfig: any) => {
	if (!buttonConfig.colorScheme?.light) return

	const light = buttonConfig.colorScheme.light

	// Apply all button types from the root configuration
	if (light.root) {
		const buttonTypes = ["primary", "secondary", "success", "info", "warning", "danger"]

		buttonTypes.forEach((type) => {
			if (light.root[type]) {
				// Map warning to warn class
				const severityClass = type === "warning" ? "p-button-warn" : `p-button-${type}`

				// Apply to normal buttons
				const buttons = document.querySelectorAll(`.component-section .${severityClass}:not(.p-button-outlined):not(.p-button-text)`)
				buttons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (light.root[type].background) {
						htmlButton.style.backgroundColor = light.root[type].background
					}
					if (light.root[type].color) {
						htmlButton.style.color = light.root[type].color
					}
					if (light.root[type].borderColor) {
						htmlButton.style.borderColor = light.root[type].borderColor
					}
				})

				// Apply to outlined buttons
				const outlinedButtons = document.querySelectorAll(`.component-section .${severityClass}.p-button-outlined`)
				outlinedButtons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (light.root[type].borderColor) {
						htmlButton.style.color = light.root[type].borderColor
						htmlButton.style.borderColor = light.root[type].borderColor
					}
					htmlButton.style.backgroundColor = "transparent"
				})

				// Apply to text buttons
				const textButtons = document.querySelectorAll(`.component-section .${severityClass}.p-button-text`)
				textButtons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (light.root[type].borderColor || light.root[type].background) {
						htmlButton.style.color = light.root[type].borderColor || light.root[type].background
					}
					htmlButton.style.backgroundColor = "transparent"
					htmlButton.style.borderColor = "transparent"
				})
			}
		})
	}

	// Apply outlined buttons ONLY in preview (legacy support)
	if (light.outlined?.primary) {
		const buttons = document.querySelectorAll(".component-section .p-button.p-button-outlined")
		buttons.forEach((button: Element) => {
			const htmlButton = button as HTMLElement
			if (light.outlined.primary.color) {
				htmlButton.style.color = light.outlined.primary.color
				htmlButton.style.borderColor = light.outlined.primary.color
			}
			htmlButton.style.backgroundColor = "transparent"
		})
	}
}

const applyChipColors = (chipConfig: any) => {
	if (!chipConfig?.colorScheme?.light?.root) {
		return
	}

	const root = chipConfig.colorScheme.light.root

	// Apply ONLY in preview
	const chips = document.querySelectorAll(".component-section .p-chip")

	// Apply immediately first
	chips.forEach((chip: Element) => {
		const htmlChip = chip as HTMLElement
		if (root.background) {
			htmlChip.style.setProperty("background-color", root.background, "important")
		}
		if (root.color) {
			htmlChip.style.setProperty("color", root.color, "important")
			// Apply to text inside chip too
			const textElement = chip.querySelector(".p-chip-text")
			if (textElement) {
				;(textElement as HTMLElement).style.setProperty("color", root.color, "important")
			}
		}
		// Apply icon color (use iconColor if available, otherwise fallback to text color)
		const iconColor = root.iconColor || root.color
		if (iconColor) {
			const iconElements = chip.querySelectorAll(".p-chip-remove-icon, .pi")
			iconElements.forEach((icon: Element) => {
				;(icon as HTMLElement).style.setProperty("color", iconColor, "important")
			})
		}
	})

	// Then apply again after a delay to override any late resets
	setTimeout(() => {
		const chipsAfterDelay = document.querySelectorAll(".component-section .p-chip")

		chipsAfterDelay.forEach((chip: Element) => {
			const htmlChip = chip as HTMLElement
			if (root.background) {
				htmlChip.style.setProperty("background-color", root.background, "important")
			}
			if (root.color) {
				htmlChip.style.setProperty("color", root.color, "important")
				// Apply to text inside chip too
				const textElement = chip.querySelector(".p-chip-text")
				if (textElement) {
					;(textElement as HTMLElement).style.setProperty("color", root.color, "important")
				}
			}
			// Apply icon color (use iconColor if available, otherwise fallback to text color)
			const iconColor = root.iconColor || root.color
			if (iconColor) {
				const iconElements = chip.querySelectorAll(".p-chip-remove-icon, .pi")

				iconElements.forEach((icon: Element) => {
					;(icon as HTMLElement).style.setProperty("color", iconColor, "important")
				})
			}
		})
	}, 500)
}

const applyMultiselectColors = (multiselectConfig: any) => {
	if (!multiselectConfig?.colorScheme?.light?.option) return

	const option = multiselectConfig.colorScheme.light.option
	// Apply ONLY in preview
	const selectedOptions = document.querySelectorAll(".component-section .multiselect-option.selected")

	selectedOptions.forEach((optionEl: Element) => {
		const htmlOption = optionEl as HTMLElement
		if (option.selectedBackground) {
			htmlOption.style.backgroundColor = option.selectedBackground
		}
		if (option.selectedColor) {
			htmlOption.style.color = option.selectedColor
		}
	})
}

const applyCheckboxColors = (checkboxConfig: any) => {
	if (!checkboxConfig?.colorScheme?.light?.root) return

	const root = checkboxConfig.colorScheme.light.root

	// Apply ONLY in preview to checked checkbox inputs
	const checkboxes = document.querySelectorAll('.component-section input[type="checkbox"]:checked')
	checkboxes.forEach((checkbox: Element) => {
		const htmlCheckbox = checkbox as HTMLElement
		if (root.checkedBackground) {
			htmlCheckbox.style.setProperty("background-color", root.checkedBackground, "important")
			htmlCheckbox.style.setProperty("border-color", root.checkedBackground, "important")

			// Apply text color for check symbol (use saved color or calculate automatically)
			const textColor = root.checkedColor || (isColorLight(root.checkedBackground) ? "#000000" : "#ffffff")
			htmlCheckbox.style.setProperty("color", textColor, "important")
		}
	})

	// Apply to PrimeVue checkbox boxes
	const checkboxBoxes = document.querySelectorAll(".p-checkbox-box")
	checkboxBoxes.forEach((box: Element) => {
		const htmlBox = box as HTMLElement
		const checkbox = box.closest(".p-checkbox")?.querySelector('input[type="checkbox"]') as HTMLInputElement
		if (checkbox && checkbox.checked && root.checkedBackground) {
			htmlBox.style.setProperty("background-color", root.checkedBackground, "important")
			htmlBox.style.setProperty("border-color", root.checkedBackground, "important")

			// Apply text color for check symbol (use saved color or calculate automatically)
			const textColor = root.checkedColor || (isColorLight(root.checkedBackground) ? "#000000" : "#ffffff")

			// Apply text color to the check icon
			const checkIcon = box.querySelector(".p-checkbox-icon") || box.querySelector("i")
			if (checkIcon) {
				;(checkIcon as HTMLElement).style.setProperty("color", textColor, "important")
			}

			// Also set CSS custom property for pseudo-elements (check symbol)
			htmlBox.style.setProperty("--p-checkbox-icon-color", textColor, "important")
			htmlBox.style.setProperty("color", textColor, "important")
		}
	})
}

const applyRadiobuttonColors = (radiobuttonConfig: any) => {
	if (!radiobuttonConfig?.colorScheme?.light?.root) return

	const root = radiobuttonConfig.colorScheme.light.root

	// Target ONLY in preview the PrimeVue radiobutton inner icon (the actual dot)
	const radiobuttonBoxes = document.querySelectorAll(".component-section .p-radiobutton-box")
	radiobuttonBoxes.forEach((box: Element) => {
		const radiobutton = box.closest(".p-radiobutton")?.querySelector('input[type="radio"]') as HTMLInputElement
		if (radiobutton && radiobutton.checked) {
			// Find and color ONLY the inner dot icon
			const innerIcon = box.querySelector(".p-radiobutton-icon")
			if (innerIcon && root.checkedBackground) {
				const iconElement = innerIcon as HTMLElement
				iconElement.style.backgroundColor = root.checkedBackground
				iconElement.style.color = root.checkedBackground
				// Ensure the icon is visible and round
				iconElement.style.borderRadius = "50%"
			}
		}
	})

	// Also use accent-color for native radio inputs
	const radiobuttons = document.querySelectorAll('input[type="radio"]:checked')
	radiobuttons.forEach((radiobutton: Element) => {
		const htmlRadiobutton = radiobutton as HTMLElement
		if (root.checkedBackground) {
			htmlRadiobutton.style.setProperty("accent-color", root.checkedBackground, "important")
		}
	})
}

const applyToggleswitchColors = (toggleswitchConfig: any) => {
	if (!toggleswitchConfig?.colorScheme?.light?.root) return

	const root = toggleswitchConfig.colorScheme.light.root
	const checkedBg = root.checkedBackground || "#6366F1"
	const uncheckedBg = root.background || "#d1d5db"
	const uncheckedHoverBg = root.hoverBackground || "#9ca3af"

	// Inject dynamic CSS for toggleswitch states (checked, unchecked and hover)
	// This CSS will apply automatically when toggle state changes
	const styleId = "toggleswitch-dynamic-styles"
	let styleElement = document.getElementById(styleId)
	if (!styleElement) {
		styleElement = document.createElement("style")
		styleElement.id = styleId
		document.head.appendChild(styleElement)
	}

	// Calculate hover color - use checkedHoverBackground if available, otherwise lighten checkedBackground
	const checkedHoverBg = root.checkedHoverBackground || lightenColor(checkedBg, 15)

	styleElement.textContent = `
		.component-section .p-toggleswitch:not(.p-toggleswitch-checked) .p-toggleswitch-slider {
			background-color: ${uncheckedBg} !important;
		}
		.component-section .p-toggleswitch:not(.p-toggleswitch-checked):hover .p-toggleswitch-slider {
			background-color: ${uncheckedHoverBg} !important;
		}
		.component-section .p-toggleswitch-checked .p-toggleswitch-slider {
			background-color: ${checkedBg} !important;
		}
		.component-section .p-toggleswitch-checked:hover .p-toggleswitch-slider {
			background-color: ${checkedHoverBg} !important;
		}
	`
}

const applyTabsColors = (tabsConfig: any) => {
	if (!tabsConfig?.colorScheme?.light?.root) return

	const root = tabsConfig.colorScheme.light.root

	// Apply ONLY to tabs inside NavigationPreview component (not global interface)
	// Find the specific component-section that contains tabs (NavigationPreview)
	const allComponentSections = document.querySelectorAll(".component-section")
	let navigationPreview: Element | null = null

	allComponentSections.forEach((section) => {
		const title = section.querySelector("h3")?.textContent
		if (title && title.includes("Navigation")) {
			navigationPreview = section
		}
	})

	if (!navigationPreview) {
		return
	}

	// Target tabs ONLY within NavigationPreview component
	const previewTabs = (navigationPreview as Element).querySelectorAll(".p-tab")
	previewTabs.forEach((tab: Element) => {
		const tabElement = tab as HTMLElement

		// First, FORCE remove all backgrounds
		tabElement.style.setProperty("background-color", "transparent", "important")
		tabElement.style.setProperty("background", "none", "important")
		tabElement.style.setProperty("background-image", "none", "important")

		// Reset all borders
		tabElement.style.removeProperty("border-bottom")

		// Reset text color
		tabElement.style.removeProperty("color")

		// Reset children
		const children = tab.querySelectorAll("*")
		children.forEach((child: Element) => {
			const childElement = child as HTMLElement
			childElement.style.setProperty("background-color", "transparent", "important")
			childElement.style.setProperty("background", "none", "important")
			childElement.style.removeProperty("color")
		})
	})

	// Now target only ACTIVE/SELECTED tabs within NavigationPreview and apply the color
	const selectedPreviewTabs = (navigationPreview as Element).querySelectorAll('.p-tab[data-p-active="true"], .p-tab[aria-selected="true"]')
	selectedPreviewTabs.forEach((tab: Element) => {
		const tabElement = tab as HTMLElement

		if (root.tab?.activeColor) {
			// Force background to stay transparent
			tabElement.style.setProperty("background-color", "transparent", "important")
			tabElement.style.setProperty("background", "none", "important")
			tabElement.style.setProperty("background-image", "none", "important")

			// Set ONLY text color
			tabElement.style.setProperty("color", root.tab.activeColor, "important")

			// Add border-bottom for underline (activeBar)
			if (root.tab?.activeBar) {
				tabElement.style.setProperty("border-bottom", `3px solid ${root.tab.activeBar}`, "important")
				tabElement.style.setProperty("border-top", "none", "important")
				tabElement.style.setProperty("border-left", "none", "important")
				tabElement.style.setProperty("border-right", "none", "important")
			}

			// Style all child elements for text color
			const textElements = tab.querySelectorAll("*")
			textElements.forEach((child: Element) => {
				const childElement = child as HTMLElement
				childElement.style.setProperty("color", root.tab.activeColor, "important")
				childElement.style.setProperty("background-color", "transparent", "important")
				childElement.style.setProperty("background", "none", "important")
			})
		}
	})
}

const applyMessageColors = (messageConfig: any) => {
	if (!messageConfig?.colorScheme?.light) return

	const light = messageConfig.colorScheme.light

	// Apply message colors ONLY to preview elements
	const severities = ["info", "success", "warn", "error"]

	severities.forEach((severity) => {
		if (light[severity] && light[severity].background) {
			// Calculate adapted color for text/border if not saved
			let textBorderColor = light[severity].color || light[severity].borderColor
			if (!textBorderColor && light[severity].background) {
				// If color not saved, calculate it from background with maximum contrast
				textBorderColor = isColorLight(light[severity].background)
					? calculateHoverColor(light[severity].background, 0.8) // Much darker (80%) if background is light
					: calculateHoverColor(light[severity].background, -0.8) // Much lighter (80%) if background is dark
			}

			// Find all message elements with this severity ONLY in MessagesPreview component
			const messageElements = document.querySelectorAll(
				`.component-section .p-message.p-message-${severity}, ` +
					`.component-section .p-message[data-pc-name="message"][data-pc-section="root"].p-message-${severity}`
			)

			messageElements.forEach((messageElement: Element) => {
				const htmlElement = messageElement as HTMLElement

				if (light[severity].background) {
					htmlElement.style.setProperty("background-color", light[severity].background, "important")
				}
				if (textBorderColor) {
					htmlElement.style.setProperty("color", textBorderColor, "important")
					htmlElement.style.setProperty("border-color", textBorderColor, "important")

					// Ensure the border is visible
					const currentBorder = window.getComputedStyle(htmlElement).borderWidth
					if (!currentBorder || currentBorder === "0px") {
						htmlElement.style.setProperty("border-width", "1px", "important")
						htmlElement.style.setProperty("border-style", "solid", "important")
					}
				}
			})
		}
	})
}

const applyToastColors = (toastConfig: any) => {
	if (!toastConfig?.colorScheme?.light?.root) return

	const root = toastConfig.colorScheme.light.root

	// Apply toast colors ONLY to preview elements
	const severities = ["info", "success", "warning", "error"]

	severities.forEach((severity) => {
		// Map severity naming (warning -> warn for CSS classes)
		const cssClass = severity === "warning" ? "warn" : severity

		if (root[severity] && root[severity].background) {
			// Calculate adapted color for text/border if not saved
			let textBorderColor = root[severity].color || root[severity].borderColor
			if (!textBorderColor && root[severity].background) {
				// If color not saved, calculate it from background with maximum contrast
				textBorderColor = isColorLight(root[severity].background)
					? calculateHoverColor(root[severity].background, 0.8) // Much darker (80%) if background is light
					: calculateHoverColor(root[severity].background, -0.8) // Much lighter (80%) if background is dark
			}

			// Find all toast preview elements with this severity ONLY in MessagesPreview component
			const toastElements = document.querySelectorAll(`.component-section .toast-preview.toast-${cssClass}`)

			toastElements.forEach((toastElement: Element) => {
				const htmlElement = toastElement as HTMLElement

				if (root[severity].background) {
					htmlElement.style.setProperty("background-color", root[severity].background, "important")
				}
				if (textBorderColor) {
					htmlElement.style.setProperty("color", textBorderColor, "important")
					htmlElement.style.setProperty("border-color", textBorderColor, "important")

					// Apply the same color to icons within this toast
					const toastIcon = toastElement.querySelector(".toast-icon i")
					if (toastIcon) {
						;(toastIcon as HTMLElement).style.setProperty("color", textBorderColor, "important")
					}

					const toastClose = toastElement.querySelector(".toast-close i")
					if (toastClose) {
						;(toastClose as HTMLElement).style.setProperty("color", textBorderColor, "important")
					}

					// Ensure the border is visible
					const currentBorder = window.getComputedStyle(htmlElement).borderWidth
					if (!currentBorder || currentBorder === "0px") {
						htmlElement.style.setProperty("border-width", "1px", "important")
						htmlElement.style.setProperty("border-style", "solid", "important")
					}
				}
			})
		}
	})
}

const applyProgressbarColors = (progressbarConfig: any) => {
	if (!progressbarConfig?.colorScheme?.light?.value) return

	const value = progressbarConfig.colorScheme.light.value
	if (!value.background) return

	// Set global CSS variables so the whole app reflects the saved colors
	document.documentElement.style.setProperty("--p-progressbar-value-background", value.background)
	if (value.color) {
		document.documentElement.style.setProperty("--p-progressbar-label-color", value.color)
	}

	// Inject a global style tag so .progress-text inside any ProgressBar uses the right color
	let styleEl = document.getElementById("global-progressbar-styles") as HTMLStyleElement
	if (!styleEl) {
		styleEl = document.createElement("style")
		styleEl.id = "global-progressbar-styles"
		document.head.appendChild(styleEl)
	}
	styleEl.textContent = `
		.p-progressbar-value { background: var(--p-progressbar-value-background) !important; }
		.p-progressbar-label, .progress-text { color: var(--p-progressbar-label-color, #ffffff) !important; }
	`
}

const applyDatatableColors = (datatableConfig: any) => {
	if (!datatableConfig.colorScheme?.light) return

	const light = datatableConfig.colorScheme.light

	// Set CSS custom properties for DataTable semantic values
	const root = document.documentElement

	// Apply header colors via CSS custom properties
	if (light.headerCell) {
		if (light.headerCell.background) {
			root.style.setProperty("--p-datatable-header-cell-background", light.headerCell.background)
		}
		if (light.headerCell.color) {
			root.style.setProperty("--p-datatable-header-cell-color", light.headerCell.color)
		}
		if (light.headerCell.borderColor) {
			root.style.setProperty("--p-datatable-header-cell-border-color", light.headerCell.borderColor)
		}
	}

	// Apply body colors via CSS custom properties
	if (light.bodyCell) {
		if (light.bodyCell.background) {
			root.style.setProperty("--p-datatable-body-cell-background", light.bodyCell.background)
		}
		if (light.bodyCell.color) {
			root.style.setProperty("--p-datatable-body-cell-color", light.bodyCell.color)
		}
		if (light.bodyCell.borderColor) {
			root.style.setProperty("--p-datatable-body-cell-border-color", light.bodyCell.borderColor)
		}
	}

	// Apply row hover and selection colors via CSS custom properties
	if (light.row) {
		if (light.row.hoverBackground) {
			root.style.setProperty("--p-datatable-row-hover-background", light.row.hoverBackground)
		}
		if (light.row.selectedBackground) {
			root.style.setProperty("--p-datatable-row-selected-background", light.row.selectedBackground)
		}
	}
}

const applyDialogColors = (dialogConfig: any) => {
	if (!dialogConfig) return

	const root = document.documentElement

	if (dialogConfig.titleBackgroundColor) {
		root.style.setProperty("--p-dialog-title-background", dialogConfig.titleBackgroundColor)
	}
	if (dialogConfig.titleTextColor) {
		root.style.setProperty("--p-dialog-title-color", dialogConfig.titleTextColor)
	}
}

const applyFormColors = (formConfig: any) => {
	if (!formConfig) return

	const root = document.documentElement

	if (formConfig.errorColor) {
		root.style.setProperty("--p-form-error-color", formConfig.errorColor)
	}
	if (formConfig.labelColor) {
		root.style.setProperty("--p-form-label-color", formConfig.labelColor)
	}
	if (formConfig.borderColor) {
		root.style.setProperty("--p-form-border-color", formConfig.borderColor)
	}
	if (formConfig.focusBorderColor) {
		root.style.setProperty("--p-form-focus-border-color", formConfig.focusBorderColor)
	}
	if (formConfig.warningIconColor) {
		root.style.setProperty("--p-form-warning-icon-color", formConfig.warningIconColor)
	}
}

const applyDownstreamDistributionColors = (downstreamConfig: any) => {
	// Initialize stepRow if it doesn't exist (for themes created before stepRow was added)
	if (!downstreamConfig.stepRow) {
		// Create a proper semantic structure with toJSON method
		const stepRowConfig = {
			backgroundColor: "#f8f9fa",
			textColor: "#000",
			init: (_data?: any) => {},
			toJSON: (data?: any) => ({ backgroundColor: "#f8f9fa", textColor: "#000" }),
		}
		downstreamConfig.stepRow = stepRowConfig
	}

	const root = document.documentElement

	// Apply stepField colors
	if (downstreamConfig.stepField) {
		// Apply to DOM elements for immediate visual feedback
		const stepCells = document.querySelectorAll(".downstream-distribution-preview .step-field-header")
		stepCells.forEach((cell: Element) => {
			const htmlCell = cell as HTMLElement
			if (downstreamConfig.stepField.backgroundColor) {
				htmlCell.style.setProperty("background-color", downstreamConfig.stepField.backgroundColor, "important")
			}
			if (downstreamConfig.stepField.textColor) {
				htmlCell.style.setProperty("color", downstreamConfig.stepField.textColor, "important")
			}
		})

		// Also apply to the empty cell in first row above Step
		const table = document.querySelector(".downstream-distribution-preview table")
		if (table) {
			const firstRow = table.querySelector("thead tr:first-child")
			if (firstRow && firstRow.children.length > 0) {
				const firstRowFirstCell = firstRow.children[0] as HTMLElement
				if (firstRowFirstCell) {
					if (downstreamConfig.stepField.backgroundColor) {
						firstRowFirstCell.style.setProperty("background-color", downstreamConfig.stepField.backgroundColor, "important")
					}
					if (downstreamConfig.stepField.textColor) {
						firstRowFirstCell.style.setProperty("color", downstreamConfig.stepField.textColor, "important")
					}
				}
			}
		}

		// Also set CSS variables for consistency
		if (downstreamConfig.stepField.backgroundColor) {
			root.style.setProperty("--p-downstream-distribution-table-step-field-background-color", downstreamConfig.stepField.backgroundColor)
		}
		if (downstreamConfig.stepField.textColor) {
			root.style.setProperty("--p-downstream-distribution-table-step-field-text-color", downstreamConfig.stepField.textColor)
		}
	}

	// Apply transportGroup colors
	if (downstreamConfig.transportGroup) {
		// Apply to DOM elements for immediate visual feedback
		const transportCells = document.querySelectorAll(
			".downstream-distribution-preview .transport-group-header, .downstream-distribution-preview .transport-group-subheader"
		)
		transportCells.forEach((cell: Element) => {
			const htmlCell = cell as HTMLElement
			if (downstreamConfig.transportGroup.backgroundColor) {
				htmlCell.style.setProperty("background-color", downstreamConfig.transportGroup.backgroundColor, "important")
			}
			if (downstreamConfig.transportGroup.textColor) {
				htmlCell.style.setProperty("color", downstreamConfig.transportGroup.textColor, "important")
			}
		})

		// Also set CSS variables for consistency
		if (downstreamConfig.transportGroup.backgroundColor) {
			root.style.setProperty("--p-downstream-distribution-table-transport-group-background-color", downstreamConfig.transportGroup.backgroundColor)
		}
		if (downstreamConfig.transportGroup.textColor) {
			root.style.setProperty("--p-downstream-distribution-table-transport-group-text-color", downstreamConfig.transportGroup.textColor)
		}
	}

	// Apply wharehousingGroup colors
	if (downstreamConfig.wharehousingGroup) {
		// Apply to DOM elements for immediate visual feedback
		const warehousingCells = document.querySelectorAll(
			".downstream-distribution-preview .warehousing-group-header, .downstream-distribution-preview .warehousing-group-subheader"
		)
		warehousingCells.forEach((cell: Element) => {
			const htmlCell = cell as HTMLElement
			if (downstreamConfig.wharehousingGroup.backgroundColor) {
				htmlCell.style.setProperty("background-color", downstreamConfig.wharehousingGroup.backgroundColor, "important")
			}
			if (downstreamConfig.wharehousingGroup.textColor) {
				htmlCell.style.setProperty("color", downstreamConfig.wharehousingGroup.textColor, "important")
			}
		})

		// Also set CSS variables for consistency
		if (downstreamConfig.wharehousingGroup.backgroundColor) {
			root.style.setProperty(
				"--p-downstream-distribution-table-wharehousing-group-background-color",
				downstreamConfig.wharehousingGroup.backgroundColor
			)
		}
		if (downstreamConfig.wharehousingGroup.textColor) {
			root.style.setProperty("--p-downstream-distribution-table-wharehousing-group-text-color", downstreamConfig.wharehousingGroup.textColor)
		}
	}

	// Apply actionField colors
	if (downstreamConfig.actionField) {
		// Apply to DOM elements for immediate visual feedback
		const actionCells = document.querySelectorAll(".downstream-distribution-preview .action-field-header")
		actionCells.forEach((cell: Element) => {
			const htmlCell = cell as HTMLElement
			if (downstreamConfig.actionField.backgroundColor) {
				htmlCell.style.setProperty("background-color", downstreamConfig.actionField.backgroundColor, "important")
			}
			if (downstreamConfig.actionField.textColor) {
				htmlCell.style.setProperty("color", downstreamConfig.actionField.textColor, "important")
			}
		})

		// Also apply to the empty cell in first row above Actions (last cell)
		const table = document.querySelector(".downstream-distribution-preview table")
		if (table) {
			const firstRow = table.querySelector("thead tr:first-child")
			if (firstRow && firstRow.children.length > 0) {
				const firstRowLastCell = firstRow.children[firstRow.children.length - 1] as HTMLElement
				if (firstRowLastCell) {
					if (downstreamConfig.actionField.backgroundColor) {
						firstRowLastCell.style.setProperty("background-color", downstreamConfig.actionField.backgroundColor, "important")
					}
					if (downstreamConfig.actionField.textColor) {
						firstRowLastCell.style.setProperty("color", downstreamConfig.actionField.textColor, "important")
					}
				}
			}
		}

		// Also set CSS variables for consistency
		if (downstreamConfig.actionField.backgroundColor) {
			root.style.setProperty("--p-downstream-distribution-table-action-field-background-color", downstreamConfig.actionField.backgroundColor)
		}
		if (downstreamConfig.actionField.textColor) {
			root.style.setProperty("--p-downstream-distribution-table-action-field-text-color", downstreamConfig.actionField.textColor)
		}
	}

	// Apply stepRow colors (only to Step rows)

	if (downstreamConfig.stepRow) {
		// Apply to preview table
		const previewTableRows = document.querySelectorAll(".downstream-distribution-preview tbody tr")

		let previewStepRowsCount = 0
		previewTableRows.forEach((row: Element) => {
			const htmlRow = row as HTMLElement
			const firstCell = htmlRow.querySelector("td:first-child")
			// Only apply to rows that contain "Step" in the first cell
			if (firstCell && firstCell.textContent && firstCell.textContent.includes("Step")) {
				previewStepRowsCount++

				if (downstreamConfig.stepRow.backgroundColor) {
					htmlRow.style.setProperty("background-color", downstreamConfig.stepRow.backgroundColor, "important")
					// Apply background color to ALL child elements within the row
					const childElements = htmlRow.querySelectorAll("*")
					childElements.forEach((child) => {
						;(child as HTMLElement).style.setProperty("background-color", downstreamConfig.stepRow.backgroundColor, "important")
					})
				}
				if (downstreamConfig.stepRow.textColor) {
					htmlRow.style.setProperty("color", downstreamConfig.stepRow.textColor, "important")
					// Apply text color to ALL child elements within the row
					const childElements = htmlRow.querySelectorAll("*")
					childElements.forEach((child) => {
						;(child as HTMLElement).style.setProperty("color", downstreamConfig.stepRow.textColor, "important")
					})
				}
			}
		})

		// Also apply to real downstream distribution table if it exists
		const realTableRows = document.querySelectorAll(".p-datatable tbody tr")

		let realStepRowsCount = 0
		realTableRows.forEach((row: Element) => {
			const htmlRow = row as HTMLElement
			const firstCell = htmlRow.querySelector("td:first-child")
			// Only apply to rows that contain "Step" in the first cell
			if (firstCell && firstCell.textContent && firstCell.textContent.includes("Step")) {
				realStepRowsCount++

				if (downstreamConfig.stepRow.backgroundColor) {
					htmlRow.style.setProperty("background-color", downstreamConfig.stepRow.backgroundColor, "important")
					// Apply background color to ALL child elements within the row
					const childElements = htmlRow.querySelectorAll("*")
					childElements.forEach((child) => {
						;(child as HTMLElement).style.setProperty("background-color", downstreamConfig.stepRow.backgroundColor, "important")
					})
				}
				if (downstreamConfig.stepRow.textColor) {
					htmlRow.style.setProperty("color", downstreamConfig.stepRow.textColor, "important")
					// Apply text color to ALL child elements within the row
					const childElements = htmlRow.querySelectorAll("*")
					childElements.forEach((child) => {
						;(child as HTMLElement).style.setProperty("color", downstreamConfig.stepRow.textColor, "important")
					})
				}
			}
		})

		// Also set CSS variables for consistency
		if (downstreamConfig.stepRow.backgroundColor) {
			root.style.setProperty("--p-downstream-distribution-table-rows-background-color", downstreamConfig.stepRow.backgroundColor)
		}
		if (downstreamConfig.stepRow.textColor) {
			root.style.setProperty("--p-downstream-distribution-table-rows-text-color", downstreamConfig.stepRow.textColor)
		}
	}
}

// Apply landing page utilities buttons colors
const applyLandingPageUtilitiesColors = (landingpageConfig: any) => {
	if (!landingpageConfig) return

	const root = document.documentElement

	// Apply normal state colors
	if (landingpageConfig.backgroundColor) {
		root.style.setProperty("--p-landingpage-utilities-buttons-background-color", landingpageConfig.backgroundColor)
	}
	if (landingpageConfig.textColor) {
		root.style.setProperty("--p-landingpage-utilities-buttons-text-color", landingpageConfig.textColor)

		// Apply to the button text directly
		const buttons = document.querySelectorAll(".landingpage-utilities-button .landingpage-utilities-text")
		buttons.forEach((button: Element) => {
			const htmlButton = button as HTMLElement
			htmlButton.style.color = landingpageConfig.textColor
		})
	}

	// Apply hover state colors
	if (landingpageConfig.backgroundColorHover) {
		root.style.setProperty("--p-landingpage-utilities-buttons-background-color-hover", landingpageConfig.backgroundColorHover)
	}
	if (landingpageConfig.textColorHover) {
		root.style.setProperty("--p-landingpage-utilities-buttons-text-color-hover", landingpageConfig.textColorHover)
	}
}

// Initialize downstream distribution table with default semantic values
const initializeDownstreamDistributionDefaults = async () => {
	if (!localThemeState.value.selectedTheme?.semantic) return

	const semantic = localThemeState.value.selectedTheme.semantic as any

	// Set accent color in colorScheme.light structure if not already set
	if (!semantic.colorScheme) {
		semantic.colorScheme = { light: { primary: "#3B82F6", secondary: "#EF4444", accent: "#6366F1" } }
	}
	if (!semantic.colorScheme.light) {
		semantic.colorScheme.light = { primary: "#3B82F6", secondary: "#EF4444", accent: "#6366F1" }
	}
	if (!semantic.colorScheme.light.accent) {
		semantic.colorScheme.light.accent = "#6366F1"
	}
	if (!semantic.customPalette) {
		semantic.customPalette = {
			3: "#8f6113",
			4: "#EF4444",
			5: "#06B6D4",
			6: "#84CC16",
			7: "#F97316",
			8: "#EC4899",
			9: "#6366F1",
		}
	}

	// Initialize downstream distribution table semantic values if not already set
	if (!semantic.downstreamDistributionTable) {
		const downstreamConfig = initializeSemanticStructure(["downstreamDistributionTable"])

		// Set default values from provided semantic object
		downstreamConfig.stepField = {
			backgroundColor: "#809bce",
			textColor: "#000",
		}
		downstreamConfig.transportGroup = {
			backgroundColor: "#95b8d1",
			textColor: "#000",
		}
		downstreamConfig.wharehousingGroup = {
			backgroundColor: "#b8e0d4",
			textColor: "#000",
		}
		downstreamConfig.actionField = {
			backgroundColor: "#d6eadf",
			textColor: "#000",
		}
		downstreamConfig.stepRow = {
			backgroundColor: "#f8f9fa",
			textColor: "#000",
		}

		// Apply the colors immediately
		await nextTick()
		applyDownstreamDistributionColors(downstreamConfig)
	}
}

// Color picker functionality (similar to login page)
const handleClick = (event: MouseEvent) => {
	const target = event.target as HTMLElement

	// Don't process clicks on the color picker itself or PrimeVue color picker overlay
	if (target.closest(".color-picker-panel") || target.closest(".p-colorpicker-panel") || target.closest(".p-colorpicker-overlay")) {
		return
	}

	// Don't process clicks on the text color preview (it has its own handler)
	if (target.classList.contains("color-preview")) {
		return
	}

	// First check if it's an interactive element that should close the picker
	if (target.classList.contains("send-color-btn") || target.classList.contains("color-picker-close")) {
		showPicker.value = false
		return
	}

	// IMPORTANT: Only allow color picker within component preview containers
	const componentSection = target.closest(".component-section")

	// If not within a component section, close picker only if it's already open and this is for component picking
	// Don't close if we're in text color mode
	if (!componentSection) {
		if (showPicker.value && currentColorType.value !== "text") {
			showPicker.value = false
		}
		return
	}

	// Check if the clicked element is a PrimeVue component or part of one
	const primevueComponent = target.closest("[class*='p-']")

	// Also check for multiselect option elements, but only selected ones
	const multiselectOption = target.closest(".multiselect-option")
	const isSelectedMultiselectOption = multiselectOption && multiselectOption.classList.contains("selected")

	// Also check for toast elements
	const toastElement = target.closest(".toast-preview")

	// Check for landing page utilities button
	const landingPageButton = target.closest(".landingpage-utilities-button") || target.closest("[data-component-type='landingpage-utilities-button']")

	// Check if click is on the text only (for text-only color picker)
	const landingPageText = target.closest(".landingpage-utilities-text") || (target.hasAttribute && target.hasAttribute("data-text-only"))

	// For checkboxes, only allow color picker on the primary checkbox (first one)
	const checkboxElement = target.closest(".p-checkbox")
	if (checkboxElement && !checkboxElement.classList.contains("checkbox-primary")) {
		showPicker.value = false
		return
	}

	// For radiobuttons, only allow color picker on the primary radiobutton (first one)
	const radiobuttonElement = target.closest(".p-radiobutton")
	if (radiobuttonElement && !radiobuttonElement.classList.contains("radiobutton-primary")) {
		showPicker.value = false
		return
	}

	// If no PrimeVue component found and not a selected multiselect option and not a toast element and not a landing page button within the component section, don't show color picker
	if (!primevueComponent && !isSelectedMultiselectOption && !toastElement && !landingPageButton) {
		showPicker.value = false
		return
	}

	// Don't allow color picking on interactive elements that should remain functional
	// Exception: Allow clicks inside DataTable cells for color picking
	const isInDataTable = target.closest(".p-datatable")
	if (
		!isInDataTable &&
		(target.closest(".p-inputtext") || target.closest(".p-dropdown") || target.closest(".p-calendar") || target.closest(".p-colorpicker"))
	) {
		showPicker.value = false
		return
	}

	// Don't allow color picker on radiobutton labels (text)
	const isRadiobuttonLabel = target.tagName === "LABEL" && target.getAttribute("for") && target.getAttribute("for").includes("option")
	if (isRadiobuttonLabel) {
		showPicker.value = false
		return
	}

	// For buttons, always target the button element, not child elements like text
	let targetElement: HTMLElement

	if (isSelectedMultiselectOption) {
		// For selected multiselect options, use the option element directly
		targetElement = multiselectOption as HTMLElement
	} else if (primevueComponent && primevueComponent.classList.contains("p-button")) {
		targetElement = primevueComponent as HTMLElement
	} else if (primevueComponent && primevueComponent.classList.contains("p-chip")) {
		// For chips, always target the chip element, not child elements like text or icons
		targetElement = primevueComponent as HTMLElement
	} else if (toastElement) {
		// For toasts, use the toast element
		targetElement = toastElement as HTMLElement
	} else {
		targetElement = primevueComponent as HTMLElement
		// Check if we're inside a button
		const buttonParent = target.closest(".p-button")
		if (buttonParent) {
			targetElement = buttonParent as HTMLElement
		}
		// Check if we're inside a chip
		const chipParent = target.closest(".p-chip")
		if (chipParent) {
			targetElement = chipParent as HTMLElement
		}
	}

	// For DataTable elements, we want to keep the original clicked element
	// so we can determine if it's header vs body and which column
	const isDataTableElement = target.closest(".p-datatable")
	if (isDataTableElement) {
		// For DataTable, keep the original clicked element for precise targeting
		targetElement = target as HTMLElement
	}

	clickedElement = targetElement

	// Special handling for ToggleSwitch - detect state and set subtitle
	// NOTE: When clicking the toggle, PrimeVue changes the state BEFORE our handler runs
	// So if we see "checked" now, the user clicked on it when it was "unchecked" (and vice versa)
	// We need to invert the logic to get the state the user was actually clicking on
	const toggleswitchElement = targetElement.closest(".p-toggleswitch")
	if (toggleswitchElement) {
		const isCurrentlyChecked = toggleswitchElement.classList.contains("p-toggleswitch-checked")
		// Invert: if currently checked, user clicked when it was unchecked
		const wasCheckedWhenClicked = !isCurrentlyChecked
		toggleswitchState.value = wasCheckedWhenClicked ? "checked" : "unchecked"
		colorPickerSubtitle.value = wasCheckedWhenClicked ? "🟢 Editing: Checked (ON) State" : "⚪ Editing: Unchecked (OFF) State"
	} else {
		// Reset subtitle for other components
		toggleswitchState.value = null
		colorPickerSubtitle.value = ""
	}

	// Special handling for Landing Page Utilities text - open text-only color picker
	if (landingPageText) {
		event.preventDefault()
		event.stopPropagation()

		// Mark this as a text color picker
		currentColorType.value = "text"
		clickedElement = landingPageText as HTMLElement

		// Get the text color from the clicked text element
		const textColor = window.getComputedStyle(clickedElement).color
		const hexColor = rgbToHex(textColor)
		selectedColor.value = hexColor
		selectedTextColor.value = hexColor
		hexInputValue.value = hexColor

		// Position the color picker relative to the clicked text
		const rect = (landingPageText as HTMLElement).getBoundingClientRect()
		pickerX.value = rect.right + 50
		pickerY.value = rect.top - 20

		// Open the picker
		showPicker.value = true

		// Add click outside listener
		setTimeout(() => {
			document.addEventListener("click", handleClickOutside)
		}, 0)
		return
	}

	// Special handling for Landing Page Utilities button (not text) - force text-only mode
	if (landingPageButton) {
		colorMode.value = "text"
		const textColor = window.getComputedStyle(clickedElement).color
		const hexColor = rgbToHex(textColor)
		selectedColor.value = hexColor
		selectedTextColor.value = hexColor
		hexInputValue.value = hexColor
	}

	// Protection against changes during template loading
	if (props.isLoadingTemplate) {
		return
	}

	if (props.selectedColor) {
		selectedColor.value = props.selectedColor.replace("#", "")
		emit("disablePasteColor")
		applyColor()
		return
	}

	// Position the color picker to the left of the element that was clicked with more distance
	const buttonRect = clickedElement.getBoundingClientRect()
	pickerX.value = buttonRect.left - 380 // 280px picker width + 100px margin
	pickerY.value = buttonRect.top - 20 // Offset slightly up for better visibility

	// Get the computed background color (skip for landing page button since we already set it above)
	let currentColor = landingPageButton ? rgbToHex(window.getComputedStyle(clickedElement).color) : window.getComputedStyle(clickedElement).backgroundColor
	let element: HTMLElement | null = clickedElement
	let attempts = 0
	const maxAttempts = 5

	while ((!currentColor || currentColor === "rgba(0, 0, 0, 0)" || currentColor === "transparent") && element && attempts < maxAttempts) {
		element = element.parentElement
		if (element) {
			currentColor = window.getComputedStyle(element).backgroundColor
		}
		attempts++
	}

	if (!currentColor || currentColor === "rgba(0, 0, 0, 0)" || currentColor === "transparent") {
		currentColor = "rgb(255, 255, 255)"
	}

	const hexColor = rgbToHex(currentColor)
	selectedColor.value = hexColor
	hexInputValue.value = hexColor

	// Remove any existing listener first
	document.removeEventListener("click", handleClickOutside)

	// Open the picker
	showPicker.value = true

	// Use setTimeout with 0 delay to ensure the current event propagation completes
	setTimeout(() => {
		document.addEventListener("click", handleClickOutside)
	}, 0)
}

const handleClickOutside = (event: MouseEvent) => {
	const target = event.target as HTMLElement

	// Don't close if clicking on the text color preview
	if (target.classList.contains("color-preview")) {
		return
	}

	if (
		!target.closest(".color-picker-panel") &&
		!target.closest(".p-colorpicker") &&
		!target.closest(".p-colorpicker-panel") &&
		!target.closest(".p-colorpicker-overlay")
	) {
		closePicker()
	}
}

const closePicker = () => {
	showPicker.value = false
	clickedElement = null
	toggleswitchState.value = null
	colorPickerSubtitle.value = ""
	document.removeEventListener("click", handleClickOutside)
	emit("saveToUndo")
}

// Switch between background and text color modes
const switchColorMode = (mode: "background" | "text", event?: Event) => {
	event?.stopPropagation()
	colorMode.value = mode

	// When switching to text mode, sync with current clicked element's text color
	if (mode === "text" && clickedElement) {
		const currentTextColor = getComputedStyle(clickedElement).color
		if (currentTextColor && currentTextColor !== "rgba(0, 0, 0, 0)" && currentTextColor !== "transparent") {
			// Convert RGB to hex
			const hexColor = rgbToHex(currentTextColor)
			selectedTextColor.value = hexColor.replace("#", "")
		} else {
			selectedTextColor.value = "374151" // Default text color
		}
	} else if (mode === "text") {
		selectedTextColor.value = "374151" // Default text color if no element is clicked
	}
}

const applyColor = () => {
	// Get the appropriate color based on current mode
	let color: string
	if (colorMode.value === "text") {
		color = selectedTextColor.value.startsWith("#") ? selectedTextColor.value : "#" + selectedTextColor.value
	} else {
		color = selectedColor.value.startsWith("#") ? selectedColor.value : "#" + selectedColor.value
	}

	if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
		return
	}

	// Handle text color picker - only if the clicked element is actually the text color input
	if (clickedElement && (clickedElement as any).dataset?.cssVar === "text-color") {
		textSettings.color = color
		hexInputValue.value = color

		// Don't close picker automatically - let user continue selecting colors
		return
	}

	// Handle landing page utilities text color picker
	if (clickedElement && (clickedElement.classList?.contains("landingpage-utilities-text") || (clickedElement as any).dataset?.textOnly === "true")) {
		// Apply color directly to the text element
		clickedElement.style.color = color

		// Update CSS variables for landing page utilities
		// Normal state: transparent background, colored text/border
		document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-background-color", "transparent")
		document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color", color)

		// Hover state: colored background (same as normal text color), white text/border
		document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-background-color-hover", color)
		document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color-hover", "#ffffff")

		// Update theme settings
		const semanticConfig = initializeSemanticStructure(["landingpageUtilitiesButtons"])
		semanticConfig.backgroundColor = "transparent"
		semanticConfig.textColor = color
		semanticConfig.backgroundColorHover = color
		semanticConfig.textColorHover = "#ffffff"

		// Process all button hover colors before saving
		processAllButtonHoverColors()

		emit("updateTheme", localThemeState.value)

		// Don't close picker automatically - let user continue selecting colors
		return
	}

	if (clickedElement) {
		// Check if it's a tab element or tab-related container
		const isTab = clickedElement.classList.contains("p-tab")
		const isFirstTab =
			isTab &&
			(clickedElement.textContent?.trim() === "Tab 1" ||
				clickedElement.getAttribute("data-p-value") === "0" ||
				clickedElement.getAttribute("value") === "0")
		// Check if element is inside NavigationPreview component specifically
		const componentSection = clickedElement.closest(".component-section")
		const sectionTitle = componentSection?.querySelector("h3")?.textContent

		const isInMessagesPreview =
			clickedElement.closest(".p-message") !== null || sectionTitle?.includes("Messages") || clickedElement.classList.contains("messages-content")

		const isInToastPreview =
			clickedElement.classList.contains("toast-preview") ||
			clickedElement.closest(".toast-preview") !== null ||
			clickedElement.closest(".toast-section") !== null

		const isInNavigationPreview =
			!isInMessagesPreview &&
			!isInToastPreview &&
			(sectionTitle?.includes("Navigation") ||
				clickedElement.closest(".tabs-section") !== null ||
				clickedElement.classList.contains("navigation-content") ||
				clickedElement.classList.contains("tabs-section"))

		const isTabContainer =
			!isInMessagesPreview &&
			isInNavigationPreview &&
			(clickedElement.classList.contains("p-tabs") ||
				clickedElement.classList.contains("p-tablist") ||
				clickedElement.classList.contains("p-tabpanels") ||
				clickedElement.classList.contains("p-tabpanel") ||
				clickedElement.classList.contains("tabs-section") ||
				clickedElement.classList.contains("navigation-content") ||
				(clickedElement.classList.contains("section-title") && clickedElement.textContent?.includes("Navigation")) ||
				clickedElement.closest(".p-tabs") !== null ||
				clickedElement.tagName === "P" ||
				clickedElement.tagName === "SMALL")

		// Only allow color picker on the FIRST tab only
		if (isFirstTab) {
			// This is an actual tab - allow color picker

			let classes = Array.from(clickedElement.classList)
			if (!classes.includes("p-tabs")) {
				classes.push("p-tabs")
			}

			// Update local theme settings for tabs
			updateLocalThemeSettings(classes, color, colorMode.value)
		} else if (isTab && !isFirstTab) {
			// This is Tab 2 or Tab 3 - disable color picker

			return // Exit early, don't process color picker
		} else if (isTabContainer) {
			// This is any element in NavigationPreview (except first tab) - disable color picker

			return // Exit early, don't process color picker
		} else if (isInToastPreview) {
			// This is a toast element - allow color picker and use

			let classes = Array.from(clickedElement.classList)
			if (!classes.includes("toast-preview")) {
				classes.push("toast-preview")
			}

			// Update local theme settings for toasts
			updateLocalThemeSettings(classes, color, colorMode.value)
		} else {
			// Normal element - apply appropriate color based on mode
			if (colorMode.value === "text") {
				clickedElement.style.setProperty("color", color, "important")
			} else {
				clickedElement.style.backgroundColor = color
			}

			// Update local theme settings based on clicked element
			updateLocalThemeSettings(Array.from(clickedElement.classList), color, colorMode.value)
		}
	}
}

// Helper function to calculate if a color is light or dark
const isColorLight = (color: string): boolean => {
	const hex = color.replace("#", "")
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 4), 16)
	const b = parseInt(hex.substring(4, 6), 16)
	const luminance = 0.299 * r + 0.587 * g + 0.114 * b
	return luminance > 128
}

// Helper function to calculate hover color (darker version)
const calculateHoverColor = (baseColor: string, factor: number = 0.2): string => {
	const hex = baseColor.replace("#", "")
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 4), 16)
	const b = parseInt(hex.substring(4, 6), 16)

	const newR = Math.round(r * (1 - factor))
	const newG = Math.round(g * (1 - factor))
	const newB = Math.round(b * (1 - factor))

	const clampedR = Math.max(0, Math.min(255, newR))
	const clampedG = Math.max(0, Math.min(255, newG))
	const clampedB = Math.max(0, Math.min(255, newB))

	const hexR = clampedR.toString(16).padStart(2, "0")
	const hexG = clampedG.toString(16).padStart(2, "0")
	const hexB = clampedB.toString(16).padStart(2, "0")

	return `#${hexR}${hexG}${hexB}`
}

// Helper function to process all button types and calculate hover colors automatically
const processAllButtonHoverColors = () => {
	console.log("Processing button hover colors...")
	// Ensure button structure exists
	if (!localThemeState.value.selectedTheme?.components?.button?.colorScheme?.light?.root) {
		return
	}

	const buttonRoot = localThemeState.value.selectedTheme.components.button.colorScheme.light.root
	const buttonTypes: (keyof ButtonRoot)[] = ["primary", "secondary", "success", "info", "warning", "danger"]

	buttonTypes.forEach((buttonType) => {
		const button = buttonRoot[buttonType] as ButtonType
		if (button && button.background) {
			// Calculate contrast color (text color based on background brightness)
			const contrastColor = isDarkColor(button.background) ? "#ffffff" : "#000000"
			button.contrastColor = contrastColor

			// Calculate hover background (slightly lighter)
			const hoverBg = lightenColor(button.background, 15)
			button.hoverBackground = hoverBg
			button.hoverBorderColor = hoverBg

			// Calculate hover text color based on hover background brightness
			const hoverTextColor = isDarkColor(hoverBg) ? "#ffffff" : "#000000"
			button.hoverColor = hoverTextColor
		}
	})
}

// Initialize semantic structure helper (following login page pattern)
const initializeSemanticStructure = (path: string[]) => {
	// Ensure we have a theme state
	if (!localThemeState.value.selectedTheme) {
		localThemeState.value.selectedTheme = new ThemeTemplate()
	}

	// Initialize semantic object if it doesn't exist
	if (!localThemeState.value.selectedTheme.semantic) {
		// Create a proper semantic object with required methods
		const semanticObj = {
			init: (_data?: any) => {},
			toJSON: () => ({}),
		} as any
		localThemeState.value.selectedTheme.semantic = semanticObj
	}

	let current = localThemeState.value.selectedTheme.semantic as any
	for (const segment of path) {
		if (!current[segment]) {
			current[segment] = {}
		}
		current = current[segment]
	}
	return current
}

// Initialize components structure (for components that go directly in selectedTheme.components)
const initializeComponentsStructure = (path: string[]) => {
	// Ensure we have a theme state
	if (!localThemeState.value.selectedTheme) {
		localThemeState.value.selectedTheme = new ThemeTemplate()
	}

	// Initialize components object if it doesn't exist
	if (!localThemeState.value.selectedTheme.components) {
		localThemeState.value.selectedTheme.components = {} as any
	}

	let current = localThemeState.value.selectedTheme.components as any
	for (let i = 0; i < path.length; i++) {
		const segment = path[i]
		if (!current[segment]) {
			// For button severity endpoints, create proper ButtonType objects
			if (
				path[0] === "button" &&
				i === path.length - 1 &&
				(segment === "help" ||
					segment === "contrast" ||
					segment === "primary" ||
					segment === "secondary" ||
					segment === "success" ||
					segment === "info" ||
					segment === "warning" ||
					segment === "danger")
			) {
				current[segment] = new ButtonType()
			} else {
				current[segment] = {}
			}
		}
		current = current[segment]
	}
	return current
}

// Update local theme settings (enhanced like login page)
const updateLocalThemeSettings = (classes: string[], color: string, mode: "background" | "text" = "background") => {
	// Map CSS classes to theme properties based on global.js structure
	const colorMappings: { [key: string]: () => void } = {
		"multiselect-option": () => {
			// Special handling for multiselect options (they don't have p-multiselect class)
			// Use the same logic as p-multiselect but access components directly
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.multiselect || typeof themeTemplate.components.multiselect.toJSON !== "function") {
				const multiselectInstance = new Multiselect()
				const colorScheme = new ColorSchemeMultiselect()
				const selectState = new SelectState()
				const option = new Option()

				selectState.option = option
				colorScheme.light = selectState
				multiselectInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).multiselect = multiselectInstance
				}
			}

			// Ensure option structure exists
			if (themeTemplate?.components?.multiselect) {
				const multiselect = themeTemplate.components.multiselect as any

				if (!multiselect.colorScheme.light) {
					const selectState = new SelectState()
					const option = new Option()
					selectState.option = option
					multiselect.colorScheme.light = selectState
				}

				if (!multiselect.colorScheme.light.option) {
					multiselect.colorScheme.light.option = new Option()
				}

				// Update multiselect option colors directly on components.multiselect
				if (mode === "text") {
					multiselect.colorScheme.light.option.selectedColor = color
					multiselect.colorScheme.light.option.selectedFocusColor = color
				} else {
					multiselect.colorScheme.light.option.selectedBackground = color
					multiselect.colorScheme.light.option.selectedFocusBackground = color
				}
			}

			// Apply to ALL selected options in all multiselects
			if (clickedElement) {
				const componentSection = clickedElement.closest(".component-section")
				if (componentSection) {
					const allSelectedOptions = componentSection.querySelectorAll(".multiselect-option.selected")
					allSelectedOptions.forEach((option: Element) => {
						const htmlOption = option as HTMLElement
						if (mode === "text") {
							htmlOption.style.color = color
						} else {
							htmlOption.style.backgroundColor = color
						}
					})
				}
			}
		},
		"p-button": () => {
			// Detect button type and severity by checking element classes
			const isOutlined = clickedElement?.classList.contains("p-button-outlined")
			const isText = clickedElement?.classList.contains("p-button-text")

			// Detect button severity
			let severity = "primary"
			if (clickedElement?.classList.contains("p-button-secondary")) severity = "secondary"
			else if (clickedElement?.classList.contains("p-button-success")) severity = "success"
			else if (clickedElement?.classList.contains("p-button-info")) severity = "info"
			else if (clickedElement?.classList.contains("p-button-warn")) severity = "warn"
			else if (clickedElement?.classList.contains("p-button-warning")) severity = "warning"
			else if (clickedElement?.classList.contains("p-button-danger")) severity = "danger"

			// Update semantic severity if it's a standard severity button
			const standardSeverities = ["primary", "secondary", "success", "info", "warning", "danger"]
			const severityName = severity === "warn" ? "warning" : severity // Map 'warn' to 'warning'

			if (standardSeverities.includes(severityName) && !isOutlined && !isText) {
				// Update the semantic.severity object
				const semanticObj = localThemeState.value.selectedTheme?.semantic as any

				// Ensure severity is a proper Severity instance
				if (!semanticObj.severity || typeof semanticObj.severity.toJSON !== "function") {
					semanticObj.severity = new Severity()
				}

				// Update the specific severity color
				semanticObj.severity[severityName] = color

				// Also update the button component configuration
				if (!localThemeState.value.selectedTheme?.components) {
					localThemeState.value.selectedTheme.components = {}
				}
				if (!localThemeState.value.selectedTheme.components.button) {
					localThemeState.value.selectedTheme.components.button = new ButtonTheme()
				}
				if (!localThemeState.value.selectedTheme.components.button.colorScheme) {
					localThemeState.value.selectedTheme.components.button.colorScheme = new ColorSchemeButton()
				}
				if (!localThemeState.value.selectedTheme.components.button.colorScheme.light) {
					localThemeState.value.selectedTheme.components.button.colorScheme.light = new ButtonState()
				}
				if (!localThemeState.value.selectedTheme.components.button.colorScheme.light.root) {
					localThemeState.value.selectedTheme.components.button.colorScheme.light.root = new ButtonRoot()
				}

				// Map severity name to ButtonRoot property name
				const severityMapping: { [key: string]: keyof ButtonRoot } = {
					primary: "primary",
					secondary: "secondary",
					success: "success",
					info: "info",
					warn: "warning",
					warning: "warning",
					danger: "danger",
				}

				const mappedSeverity = severityMapping[severityName]

				// Use mappedSeverity if available, otherwise use severityName directly
				const severityKey = mappedSeverity || severityName

				// Preserve existing button type configuration for this severity
				let buttonType = (localThemeState.value.selectedTheme.components.button.colorScheme.light.root as any)[severityKey]
				if (!buttonType || typeof buttonType.toJSON !== "function") {
					buttonType = new ButtonType()
				}

				if (mode === "text") {
					buttonType.color = color
					buttonType.activeColor = color
					buttonType.disabledColor = color
				} else {
					// Set background color
					buttonType.background = color
					buttonType.borderColor = color
					buttonType.activeBackground = color
					buttonType.disabledBackground = color
					buttonType.disabledBorderColor = color

					// Automatically calculate hover background (slightly lighter)
					const hoverBg = lightenColor(color, 15)
					buttonType.hoverBackground = hoverBg
					buttonType.hoverBorderColor = hoverBg

					// Automatically calculate hover text color based on hover background brightness
					const hoverTextColor = isDarkColor(hoverBg) ? "#ffffff" : "#000000"
					buttonType.hoverColor = hoverTextColor
				}

				// Store in the appropriate severity slot
				;(localThemeState.value.selectedTheme.components.button.colorScheme.light.root as any)[severityKey] = buttonType

				// Apply to ALL buttons with this severity in the preview
				const severityClass = severity === "warning" ? "p-button-warn" : `p-button-${severity}`
				const allSeverityButtons = document.querySelectorAll(`.${severityClass}:not(.p-button-outlined):not(.p-button-text)`)
				allSeverityButtons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (mode === "text") {
						htmlButton.style.setProperty("color", color, "important")
					} else {
						htmlButton.style.setProperty("background-color", color, "important")
						htmlButton.style.setProperty("border-color", color, "important")
					}
				})

				// Also update outlined versions
				const allOutlinedButtons = document.querySelectorAll(`.${severityClass}.p-button-outlined`)
				allOutlinedButtons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (mode === "text") {
						htmlButton.style.color = color
					} else {
						// In background mode for outlined buttons, change border color only
						// and keep the text color matching the border for consistency
						htmlButton.style.borderColor = color
						htmlButton.style.color = color
					}
					htmlButton.style.backgroundColor = "transparent"
				})

				// Also update text versions
				const allTextButtons = document.querySelectorAll(`.${severityClass}.p-button-text`)
				allTextButtons.forEach((button: Element) => {
					const htmlButton = button as HTMLElement
					if (mode === "text") {
						htmlButton.style.color = color
					} else {
						// For text buttons, background mode should still change the text color
						// since text buttons have transparent background by design
						htmlButton.style.color = color
					}
					htmlButton.style.backgroundColor = "transparent"
					htmlButton.style.borderColor = "transparent"
				})
			}

			if (isOutlined) {
				// For outlined buttons: set border and text color to the same color, keep background transparent
				const buttonConfig = initializeComponentsStructure(["button", "colorScheme", "light", "outlined", severity])
				buttonConfig.color = color
				buttonConfig.borderColor = color
				buttonConfig.hoverColor = color
				buttonConfig.hoverBorderColor = calculateHoverColor(color)
				buttonConfig.activeColor = color
				buttonConfig.activeBorderColor = calculateHoverColor(color)

				// Update semantic severity if applicable
				if (standardSeverities.includes(severityName)) {
					const semanticObj = localThemeState.value.selectedTheme?.semantic as any

					// Ensure severity is a proper Severity instance
					if (!semanticObj.severity || typeof semanticObj.severity.toJSON !== "function") {
						semanticObj.severity = new Severity()
					}

					// Update the specific severity color
					semanticObj.severity[severityName] = color

					// Apply to all buttons with this severity
					const severityClass = severity === "warning" ? "p-button-warn" : `p-button-${severity}`

					// Update all outlined buttons with this severity
					const allOutlinedButtons = document.querySelectorAll(`.${severityClass}.p-button-outlined`)
					allOutlinedButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						htmlButton.style.color = color
						htmlButton.style.borderColor = color
						htmlButton.style.backgroundColor = "transparent"
					})

					// Also update normal and text versions
					const allNormalButtons = document.querySelectorAll(`.${severityClass}:not(.p-button-outlined):not(.p-button-text)`)
					allNormalButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						if (mode === "text") {
							htmlButton.style.color = color
						} else {
							htmlButton.style.backgroundColor = color
							htmlButton.style.borderColor = color
						}
					})

					const allTextButtons = document.querySelectorAll(`.${severityClass}.p-button-text`)
					allTextButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						htmlButton.style.color = color
						htmlButton.style.backgroundColor = "transparent"
						htmlButton.style.borderColor = "transparent"
					})
				}

				// Don't apply directly here - let the new system with mode parameter handle it
				// This prevents conflicts with the mode-aware system
			} else if (isText) {
				// For text buttons: set only text color
				const buttonConfig = initializeComponentsStructure(["button", "colorScheme", "light", "text", severity])
				buttonConfig.color = color
				buttonConfig.hoverColor = calculateHoverColor(color)
				buttonConfig.activeColor = calculateHoverColor(color)

				// Update semantic severity if applicable
				if (standardSeverities.includes(severityName)) {
					const semanticObj = localThemeState.value.selectedTheme?.semantic as any

					// Ensure severity is a proper Severity instance
					if (!semanticObj.severity || typeof semanticObj.severity.toJSON !== "function") {
						semanticObj.severity = new Severity()
					}

					// Update the specific severity color
					semanticObj.severity[severityName] = color

					// Apply to all buttons with this severity
					const severityClass = severity === "warning" ? "p-button-warn" : `p-button-${severity}`

					// Update all text buttons with this severity
					const allTextButtons = document.querySelectorAll(`.${severityClass}.p-button-text`)
					allTextButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						htmlButton.style.color = color
						htmlButton.style.backgroundColor = "transparent"
						htmlButton.style.borderColor = "transparent"
					})

					// Also update normal and outlined versions
					const allNormalButtons = document.querySelectorAll(`.${severityClass}:not(.p-button-outlined):not(.p-button-text)`)
					allNormalButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						if (mode === "text") {
							htmlButton.style.color = color
						} else {
							htmlButton.style.backgroundColor = color
							htmlButton.style.borderColor = color
						}
					})

					const allOutlinedButtons = document.querySelectorAll(`.${severityClass}.p-button-outlined`)
					allOutlinedButtons.forEach((button: Element) => {
						const htmlButton = button as HTMLElement
						htmlButton.style.color = color
						htmlButton.style.borderColor = color
						htmlButton.style.backgroundColor = "transparent"
					})
				}

				// Don't apply directly here - let the new system with mode parameter handle it
				// This prevents conflicts with the mode-aware system
			} else {
				// For normal buttons: set background and automatically calculate text color based on luminance
				const buttonConfig = initializeComponentsStructure(["button", "colorScheme", "light", "root", severity])
				buttonConfig.background = color
				buttonConfig.hoverBackground = calculateHoverColor(color)
				buttonConfig.borderColor = color
				buttonConfig.hoverBorderColor = calculateHoverColor(color)
				// Don't automatically calculate text color - let it be set manually
				// buttonConfig.color and activeColor should be set manually via text mode
				buttonConfig.activeBackground = calculateHoverColor(color)

				// Don't apply directly here - let the new system with mode parameter handle it
				// This prevents conflicts with the mode-aware system
			}
		},

		"p-checkbox": () => {
			// Ensure checkbox structure exists and is properly initialized directly on components
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.checkbox || typeof themeTemplate.components.checkbox.toJSON !== "function") {
				const checkboxInstance = new Checkbox()
				const colorScheme = new ColorSchemeCheckbox()
				const lightState = new CheckboxState()
				const checkboxRoot = new CheckboxRoot()

				lightState.root = checkboxRoot
				colorScheme.light = lightState
				checkboxInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).checkbox = checkboxInstance
				}
			}

			// Update checkbox colors directly on components.checkbox
			if (themeTemplate?.components?.checkbox) {
				const checkbox = themeTemplate.components.checkbox as any
				if (mode === "text") {
					checkbox.colorScheme.light.root.checkedColor = color
				} else {
					checkbox.colorScheme.light.root.checkedBackground = color
					checkbox.colorScheme.light.root.hoverBackground = calculateHoverColor(color, 0.1)
					// Keep background transparent or white for unchecked state
					checkbox.colorScheme.light.root.background = checkbox.colorScheme.light.root.background || "#ffffff"
				}
			}

			// Apply to checked checkbox elements specifically
			if (clickedElement) {
				// Find all checked checkboxes in the component section
				const componentSection = clickedElement.closest(".component-section")
				if (componentSection) {
					const checkedCheckboxes = componentSection.querySelectorAll('input[type="checkbox"]:checked')

					checkedCheckboxes.forEach((checkbox: Element) => {
						const htmlCheckbox = checkbox as HTMLElement
						if (mode === "text") {
							htmlCheckbox.style.setProperty("color", color, "important")
						} else {
							// Apply color to the checkbox input itself
							htmlCheckbox.style.setProperty("background-color", color, "important")
							htmlCheckbox.style.setProperty("border-color", color, "important")
						}
					})

					// Also apply to checkbox icons/indicators (PrimeVue checkbox styling)
					const checkboxBoxes = componentSection.querySelectorAll(".p-checkbox-box")
					checkboxBoxes.forEach((box: Element) => {
						const htmlBox = box as HTMLElement
						const checkbox = box.closest(".p-checkbox")?.querySelector('input[type="checkbox"]') as HTMLInputElement
						if (checkbox && checkbox.checked) {
							if (mode === "text") {
								// Apply text color to the check icon
								const checkIcon = box.querySelector(".p-checkbox-icon") || box.querySelector("i")
								if (checkIcon) {
									;(checkIcon as HTMLElement).style.setProperty("color", color, "important")
								}

								// Also set CSS custom property for pseudo-elements (check symbol)
								htmlBox.style.setProperty("--p-checkbox-icon-color", color, "important")
								htmlBox.style.setProperty("color", color, "important")
							} else {
								htmlBox.style.setProperty("background-color", color, "important")
								htmlBox.style.setProperty("border-color", color, "important")
							}
						}
					})
				}
			}
		},

		"p-radiobutton": () => {
			// Ensure radiobutton structure exists for saving theme colors
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.radiobutton || typeof themeTemplate.components.radiobutton.toJSON !== "function") {
				const radiobuttonInstance = new Radiobutton()
				const colorScheme = new ColorSchemeRadiobutton()
				const lightState = new RadiobuttonState()
				const radiobuttonRoot = new RadiobuttonRoot()

				lightState.root = radiobuttonRoot
				colorScheme.light = lightState
				radiobuttonInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).radiobutton = radiobuttonInstance
				}
			}

			// Save the checked background color (for the inner dot) in the theme structure
			if (themeTemplate?.components?.radiobutton) {
				const radiobutton = themeTemplate.components.radiobutton as any
				radiobutton.colorScheme.light.root.checkedBackground = color
				radiobutton.colorScheme.light.root.checkedBorderColor = color
			}

			// For radiobuttons, apply color only to the inner dot/circle of checked radiobuttons
			if (clickedElement) {
				// Find the component section
				const componentSection = clickedElement.closest(".component-section")
				if (componentSection) {
					// Target the PrimeVue radiobutton inner icon (the actual dot)
					const radiobuttonBoxes = componentSection.querySelectorAll(".p-radiobutton-box")
					radiobuttonBoxes.forEach((box: Element) => {
						const radiobutton = box.closest(".p-radiobutton")?.querySelector('input[type="radio"]') as HTMLInputElement
						if (radiobutton && radiobutton.checked) {
							// Find and color ONLY the inner dot icon
							const innerIcon = box.querySelector(".p-radiobutton-icon")
							if (innerIcon) {
								const iconElement = innerIcon as HTMLElement
								iconElement.style.backgroundColor = color
								iconElement.style.color = color
								// Ensure the icon is visible
								iconElement.style.borderRadius = "50%"
							}
						}
					})

					// Also use accent-color for native radio inputs
					const checkedRadiobuttons = componentSection.querySelectorAll('input[type="radio"]:checked')
					checkedRadiobuttons.forEach((radiobutton: Element) => {
						const htmlRadiobutton = radiobutton as HTMLElement
						htmlRadiobutton.style.setProperty("accent-color", color, "important")
					})
				}
			}
		},

		"p-toggleswitch": () => {
			// Ensure toggleswitch structure exists and is properly initialized directly on components
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.toggleswitch || typeof themeTemplate.components.toggleswitch.toJSON !== "function") {
				const toggleswitchInstance = new Toggleswitch()
				const colorScheme = new ColorSchemeToggleswitch()
				const lightState = new ToggleswitchState()
				const toggleswitchRoot = new ToggleswitchRoot()
				const handle = new ToggleswitchHandle()

				toggleswitchRoot.handle = handle
				lightState.root = toggleswitchRoot
				colorScheme.light = lightState
				toggleswitchInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).toggleswitch = toggleswitchInstance
				}
			}

			// Use the toggleswitchState that was set when user clicked (already inverted correctly)
			const isEditingCheckedState = toggleswitchState.value === "checked"

			// Update toggleswitch colors based on the state user wanted to edit
			if (themeTemplate?.components?.toggleswitch) {
				const toggleswitch = themeTemplate.components.toggleswitch as any

				if (isEditingCheckedState) {
					// User clicked on checked state - update checked colors
					toggleswitch.colorScheme.light.root.checkedBackground = color
					toggleswitch.colorScheme.light.root.checkedBorderColor = color
					toggleswitch.colorScheme.light.root.checkedHoverBackground = lightenColor(color, 15)
				} else {
					// User clicked on unchecked state - update unchecked colors
					toggleswitch.colorScheme.light.root.background = color
					toggleswitch.colorScheme.light.root.borderColor = color
					toggleswitch.colorScheme.light.root.hoverBackground = lightenColor(color, 15)
				}

				// Handle colors remain white/light for contrast
				if (!toggleswitch.colorScheme.light.root.handle) {
					toggleswitch.colorScheme.light.root.handle = new ToggleswitchHandle()
				}
				toggleswitch.colorScheme.light.root.handle.background = "#ffffff"
				toggleswitch.colorScheme.light.root.handle.checkedBackground = "#ffffff"
			}

			// Get current colors for CSS
			const checkedBg = themeTemplate?.components?.toggleswitch?.colorScheme?.light?.root?.checkedBackground || "#6366F1"
			const checkedHoverBg = themeTemplate?.components?.toggleswitch?.colorScheme?.light?.root?.checkedHoverBackground || lightenColor(checkedBg, 15)
			const uncheckedBg = themeTemplate?.components?.toggleswitch?.colorScheme?.light?.root?.background || "#d1d5db"
			const uncheckedHoverBg = themeTemplate?.components?.toggleswitch?.colorScheme?.light?.root?.hoverBackground || "#9ca3af"

			// Update dynamic CSS for toggleswitch states (checked, unchecked, hover)
			const styleId = "toggleswitch-dynamic-styles"
			let styleElement = document.getElementById(styleId)
			if (!styleElement) {
				styleElement = document.createElement("style")
				styleElement.id = styleId
				document.head.appendChild(styleElement)
			}
			styleElement.textContent = `
				.component-section .p-toggleswitch:not(.p-toggleswitch-checked) .p-toggleswitch-slider {
					background-color: ${uncheckedBg} !important;
				}
				.component-section .p-toggleswitch:not(.p-toggleswitch-checked):hover .p-toggleswitch-slider {
					background-color: ${uncheckedHoverBg} !important;
				}
				.component-section .p-toggleswitch-checked .p-toggleswitch-slider {
					background-color: ${checkedBg} !important;
				}
				.component-section .p-toggleswitch-checked:hover .p-toggleswitch-slider {
					background-color: ${checkedHoverBg} !important;
				}
			`
		},

		"p-tabs": () => {
			// Ensure tabs structure exists and is properly initialized directly on components
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.tabs || typeof themeTemplate.components.tabs.toJSON !== "function") {
				// Create proper Tabs instance with all required classes
				const tabsInstance = new Tabs()
				const colorScheme = new ColorSchemeTabs()
				const lightState = new TabsLight()
				const tabsRoot = new TabsRoot()
				const activeBar = new ActiveBar()
				const tab = new Tab()

				// Initialize ActiveBar for underline
				activeBar.background = color

				// Initialize Tab with color for text and active bar - this is the main path
				tab.activeColor = color // Text color
				tab.activeBar = color // Line under the tab
				tab.activeBackground = "transparent" // Keep background transparent

				// Build the structure
				tabsRoot.activeBar = activeBar
				tabsRoot.tab = tab
				lightState.root = tabsRoot
				colorScheme.light = lightState
				tabsInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).tabs = tabsInstance
				}
			} else {
				// Update existing tabs configuration
				const tabsConfig = themeTemplate.components.tabs as any

				// Ensure nested structure exists
				if (!tabsConfig.colorScheme) tabsConfig.colorScheme = new ColorSchemeTabs()
				if (!tabsConfig.colorScheme.light) tabsConfig.colorScheme.light = new TabsLight()
				if (!tabsConfig.colorScheme.light.root) tabsConfig.colorScheme.light.root = new TabsRoot()
				if (!tabsConfig.colorScheme.light.root.activeBar) tabsConfig.colorScheme.light.root.activeBar = new ActiveBar()
				if (!tabsConfig.colorScheme.light.root.tab) tabsConfig.colorScheme.light.root.tab = new Tab()

				// Update colors - text and active bar, NO background
				tabsConfig.colorScheme.light.root.activeBar.background = color
				tabsConfig.colorScheme.light.root.tab.activeColor = color
				tabsConfig.colorScheme.light.root.tab.activeBar = color
				tabsConfig.colorScheme.light.root.tab.activeBackground = "transparent"
			}

			// Apply the tabs colors immediately using the same function as saved themes
			if (localThemeState.value.selectedTheme?.components?.tabs) {
				applyTabsColors(localThemeState.value.selectedTheme.components.tabs)
			}
		},

		"p-select": () => {
			const selectConfig = initializeComponentsStructure(["select", "colorScheme", "light"])
			if (!selectConfig.option) selectConfig.option = {}

			if (mode === "background") {
				selectConfig.option.selectedBackground = color
				selectConfig.option.selectedFocusBackground = color
				selectConfig.option.hoverBackground = calculateHoverColor(color, 0.1)

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.backgroundColor = color
				}
			} else if (mode === "text") {
				selectConfig.option.selectedColor = color
				selectConfig.option.selectedFocusColor = color
				selectConfig.option.hoverColor = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.color = color
				}
			}
		},

		"p-listbox": () => {
			const listboxConfig = initializeComponentsStructure(["listbox", "colorScheme", "light"])
			if (!listboxConfig.option) listboxConfig.option = {}

			if (mode === "background") {
				listboxConfig.option.selectedBackground = color
				listboxConfig.option.selectedFocusBackground = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.backgroundColor = color
				}
			} else if (mode === "text") {
				listboxConfig.option.selectedColor = color
				listboxConfig.option.selectedFocusColor = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.color = color
				}
			}
		},

		"p-multiselect": () => {
			// Ensure multiselect structure exists and is properly initialized directly on components
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.multiselect || typeof themeTemplate.components.multiselect.toJSON !== "function") {
				const multiselectInstance = new Multiselect()
				const colorScheme = new ColorSchemeMultiselect()
				const selectState = new SelectState()
				const option = new Option()

				selectState.option = option
				colorScheme.light = selectState
				multiselectInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).multiselect = multiselectInstance
				}
			}

			// Ensure option structure exists
			if (themeTemplate?.components?.multiselect) {
				const multiselect = themeTemplate.components.multiselect as any

				if (!multiselect.colorScheme.light) {
					const selectState = new SelectState()
					const option = new Option()
					selectState.option = option
					multiselect.colorScheme.light = selectState
				}

				if (!multiselect.colorScheme.light.option) {
					multiselect.colorScheme.light.option = new Option()
				}

				// Update multiselect option colors directly on components.multiselect
				if (mode === "text") {
					multiselect.colorScheme.light.option.selectedColor = color
					multiselect.colorScheme.light.option.selectedFocusColor = color
				} else {
					multiselect.colorScheme.light.option.selectedBackground = color
					multiselect.colorScheme.light.option.selectedFocusBackground = color
					multiselect.colorScheme.light.option.hoverBackground = calculateHoverColor(color, 0.1)
				}
			}

			// Apply to ALL selected options in all multiselects when any selected option is clicked
			if (clickedElement) {
				const componentSection = clickedElement.closest(".component-section")
				if (componentSection) {
					// Always update all selected options when multiselect color logic is triggered
					const allSelectedOptions = componentSection.querySelectorAll(".multiselect-option.selected")
					allSelectedOptions.forEach((option: Element) => {
						const htmlOption = option as HTMLElement
						if (mode === "text") {
							htmlOption.style.color = color
						} else {
							htmlOption.style.backgroundColor = color
						}
					})
				}
			}
		},

		"p-datatable": () => {
			const datatableConfig = initializeComponentsStructure(["datatable", "colorScheme", "light"])

			// Initialize with default values if not set
			if (!datatableConfig.headerCell) {
				datatableConfig.headerCell = {}
			}
			datatableConfig.headerCell.background = datatableConfig.headerCell.background || "#f8f9fa"
			datatableConfig.headerCell.color = datatableConfig.headerCell.color || "#495057"
			datatableConfig.headerCell.borderColor = datatableConfig.headerCell.borderColor || "#dee2e6"

			if (!datatableConfig.bodyCell) {
				datatableConfig.bodyCell = {}
			}
			datatableConfig.bodyCell.background = datatableConfig.bodyCell.background || "#ffffff"
			datatableConfig.bodyCell.color = datatableConfig.bodyCell.color || "#495057"
			datatableConfig.bodyCell.borderColor = datatableConfig.bodyCell.borderColor || "#dee2e6"
			if (!datatableConfig.row) {
				datatableConfig.row = {
					hoverBackground: "#f8f9fa",
					selectedBackground: "#e3f2fd",
				}
			}

			if (!clickedElement) return

			// Find the table first
			const table = clickedElement.closest(".p-datatable")
			if (!table) return

			// Check if clicked element is in header (including any child elements of header cells)
			const headerCell = clickedElement.closest("thead th") || clickedElement.closest("th")
			const isHeaderElement = !!headerCell

			// Check if clicked element is in tbody (including any child elements of body cells)
			const tbodyElement = clickedElement.closest("tbody")
			const bodyCell = clickedElement.closest("td")
			const isBodyElement = !!(tbodyElement && bodyCell)

			const root = document.documentElement

			if (isHeaderElement) {
				if (mode === "background") {
					// Header clicked: change entire header row background
					datatableConfig.headerCell.background = color
					datatableConfig.headerCell.hoverBackground = calculateHoverColor(color, 0.1)

					// Set CSS custom properties
					root.style.setProperty("--p-datatable-header-cell-background", color)

					// Also apply directly for immediate visual feedback
					const allHeaderCells = table.querySelectorAll("thead th")
					allHeaderCells.forEach((cell: Element) => {
						const htmlCell = cell as HTMLElement
						htmlCell.style.backgroundColor = color
					})
				} else if (mode === "text") {
					// Header clicked: change entire header row text color
					datatableConfig.headerCell.color = color

					// Set CSS custom properties
					root.style.setProperty("--p-datatable-header-cell-color", color)

					// Also apply directly for immediate visual feedback
					const allHeaderCells = table.querySelectorAll("thead th")

					// Apply immediately and also with timeout to ensure it persists
					const applyColorToHeaders = () => {
						allHeaderCells.forEach((cell: Element, index: number) => {
							const htmlCell = cell as HTMLElement

							// Only apply to content inside the cell, not the TH itself
							// because something is overriding TH colors
							const content = htmlCell.querySelector(".p-datatable-column-header-content")
							if (content) {
								const htmlContent = content as HTMLElement
								htmlContent.style.setProperty("color", color, "important")
							}

							// Also apply to any other text elements in the header
							const allTextElements = htmlCell.querySelectorAll("*")
							allTextElements.forEach((el: Element) => {
								const htmlEl = el as HTMLElement
								if (htmlEl !== content) {
									// Don't duplicate on content we already styled
									htmlEl.style.setProperty("color", color, "important")
								}
							})
						})
					}

					applyColorToHeaders()

					// Also apply after a short delay to override any other CSS that might apply later
					setTimeout(applyColorToHeaders, 100)
				}
			} else if (isBodyElement) {
				if (mode === "background") {
					// Body cell clicked: change all body cells background
					datatableConfig.bodyCell.background = color

					// Set CSS custom properties
					root.style.setProperty("--p-datatable-body-cell-background", color)

					// Also apply directly for immediate visual feedback
					const allBodyCells = table.querySelectorAll("tbody td")
					allBodyCells.forEach((cell: Element) => {
						const htmlCell = cell as HTMLElement
						htmlCell.style.setProperty("background-color", color, "important")
					})
				} else if (mode === "text") {
					// Body cell clicked: change all body cells text color
					datatableConfig.bodyCell.color = color

					// Set CSS custom properties
					root.style.setProperty("--p-datatable-body-cell-color", color)

					// Also apply directly for immediate visual feedback
					const allBodyCells = table.querySelectorAll("tbody td")
					allBodyCells.forEach((cell: Element) => {
						const htmlCell = cell as HTMLElement
						htmlCell.style.setProperty("color", color, "important")
					})
				}
			}

			// Process all button hover colors before saving
			processAllButtonHoverColors()

			// Emit theme update to save changes
			emit("updateTheme", localThemeState.value)
		},

		"p-treetable": () => {
			const treetableConfig = initializeComponentsStructure(["treetable", "colorScheme", "light"])
			if (!treetableConfig.headerCell) treetableConfig.headerCell = {}

			if (mode === "background") {
				treetableConfig.headerCell.background = color
				treetableConfig.headerCell.hoverBackground = calculateHoverColor(color, 0.1)

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.backgroundColor = color
				}
			} else if (mode === "text") {
				treetableConfig.headerCell.color = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.color = color
				}
			}
		},

		"p-progressbar": () => {
			if (!clickedElement) return

			// Initialize progressbar structure if not present using plain objects
			const themeTemplate = localThemeState.value.selectedTheme
			if (!themeTemplate?.components) {
				themeTemplate.components = {}
			}
			if (!themeTemplate.components.progressbar) {
				themeTemplate.components.progressbar = {
					colorScheme: {
						light: {
							value: {
								background: "",
								color: "",
								borderColor: "",
								init: function (_data?: any) {
									return this
								},
								toJSON: function (data?: any) {
									return {
										background: this.background,
										color: this.color,
										borderColor: this.borderColor,
									}
								},
							},
						},
					},
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							colorScheme: {
								light: {
									value: this.colorScheme.light.value,
								},
							},
						}
					},
				}
			}

			const progressConfig = themeTemplate.components.progressbar.colorScheme.light.value

			// Update progressbar colors - background is what user selects, text adapts automatically
			// Same rule as buttons: dark color → white text, light color → black text
			const isLight = isColorLight(color)
			const textColor = isLight ? "#000000" : "#ffffff"

			progressConfig.background = color
			progressConfig.color = textColor
			progressConfig.borderColor = calculateHoverColor(color, 0.1)

			// Apply globally via CSS variables + injected style tag
			applyProgressbarColors(themeTemplate.components.progressbar)
		},

		"p-message": () => {
			if (!clickedElement) return

			// Determine the message severity from clicked element or its parents
			let severity = "info" // default
			const messageElement = clickedElement.closest(".p-message")
			if (messageElement) {
				if (messageElement.classList.contains("p-message-success")) {
					severity = "success"
				} else if (messageElement.classList.contains("p-message-warn")) {
					severity = "warn"
				} else if (messageElement.classList.contains("p-message-error")) {
					severity = "error"
				} else if (messageElement.classList.contains("p-message-info")) {
					severity = "info"
				}
			}

			// Initialize message structure if not present using plain objects
			const themeTemplate = localThemeState.value.selectedTheme
			if (!themeTemplate?.components) {
				themeTemplate.components = {}
			}
			if (!themeTemplate.components.message) {
				// Create message structure compatible with NSwag serialization
				const createSeverityObject = () => ({
					background: "",
					color: "",
					borderColor: "",
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							background: this.background,
							color: this.color,
							borderColor: this.borderColor,
						}
					},
				})

				const createMessageState = () => ({
					info: createSeverityObject(),
					success: createSeverityObject(),
					warn: createSeverityObject(),
					error: createSeverityObject(),
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							info: this.info.toJSON(data),
							success: this.success.toJSON(data),
							warn: this.warn.toJSON(data),
							error: this.error.toJSON(data),
						}
					},
				})

				const createColorScheme = () => ({
					light: createMessageState(),
					dark: createMessageState(),
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							light: this.light.toJSON(data),
							dark: this.dark.toJSON(data),
						}
					},
				})

				themeTemplate.components.message = {
					colorScheme: createColorScheme(),
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							colorScheme: this.colorScheme.toJSON(data),
						}
					},
				}
			}

			const messageConfig = themeTemplate.components.message.colorScheme.light

			// Initialize severity object if not present
			if (!messageConfig[severity as keyof typeof messageConfig]) {
				;(messageConfig as any)[severity] = {
					background: "",
					color: "",
					borderColor: "",
					init: function (_data?: any) {
						return this
					},
					toJSON: function (data?: any) {
						return {
							background: this.background,
							color: this.color,
							borderColor: this.borderColor,
						}
					},
				}
			}

			// Update message colors - background is what user selects, text and border adapt automatically
			// Text and border should be at opposite ends of the color scale for maximum contrast
			const adaptedColor = isColorLight(color)
				? calculateHoverColor(color, 0.8) // Make much darker (80%) if background is light
				: calculateHoverColor(color, -0.8) // Make much lighter (80%) if background is dark

			;(messageConfig as any)[severity].background = color
			;(messageConfig as any)[severity].color = adaptedColor
			;(messageConfig as any)[severity].borderColor = adaptedColor

			// Apply ONLY to the clicked message element in the preview section
			if (messageElement) {
				const htmlElement = messageElement as HTMLElement
				htmlElement.style.setProperty("background-color", color, "important")
				htmlElement.style.setProperty("color", adaptedColor, "important")
				htmlElement.style.setProperty("border-color", adaptedColor, "important")

				// Also ensure the border is visible by setting a border width if not present
				const currentBorder = window.getComputedStyle(htmlElement).borderWidth
				if (!currentBorder || currentBorder === "0px") {
					htmlElement.style.setProperty("border-width", "1px", "important")
					htmlElement.style.setProperty("border-style", "solid", "important")
				}
			}
		},

		"p-chip": () => {
			// Ensure chip structure exists and is properly initialized directly on components
			const themeTemplate = localThemeState.value.selectedTheme

			if (!themeTemplate?.components) {
				if (themeTemplate) {
					;(themeTemplate as any).components = {}
				}
			}

			if (!themeTemplate?.components?.chip || typeof themeTemplate.components.chip.toJSON !== "function") {
				const chipInstance = new Chip()
				const colorScheme = new ColorSchemeChip()
				const lightState = new ChipState()
				const chipRoot = new ChipRoot()

				lightState.root = chipRoot
				colorScheme.light = lightState
				chipInstance.colorScheme = colorScheme

				if (themeTemplate?.components) {
					;(themeTemplate.components as any).chip = chipInstance
				}
			}

			// Update chip colors directly on components.chip
			if (themeTemplate?.components?.chip) {
				const chip = themeTemplate.components.chip as any

				if (mode === "background") {
					chip.colorScheme.light.root.background = color
					// Calculate hover background (slightly lighter)
					chip.colorScheme.light.root.hoverBackground = calculateHoverColor(color, -0.1)

					// Auto-calculate text color based on background luminance
					const isLight = isColorLight(color)
					const textColor = isLight ? "#000000" : "#ffffff"
					chip.colorScheme.light.root.color = textColor
					chip.colorScheme.light.root.iconColor = textColor

					// Hover text color should adapt to hover background
					const hoverBgColor = chip.colorScheme.light.root.hoverBackground
					const isHoverLight = isColorLight(hoverBgColor)
					chip.colorScheme.light.root.hoverColor = isHoverLight ? "#000000" : "#ffffff"
				} else if (mode === "text") {
					chip.colorScheme.light.root.color = color
					chip.colorScheme.light.root.iconColor = color // Icon color same as text color
				}
			}

			// Apply to ALL chips in the component section
			if (clickedElement) {
				const componentSection = clickedElement.closest(".component-section")
				if (componentSection) {
					// Find all chips in the entire selection components section
					const allChips = componentSection.querySelectorAll(".p-chip")
					allChips.forEach((chip: Element) => {
						const htmlChip = chip as HTMLElement

						if (mode === "background") {
							// Apply background color
							htmlChip.style.setProperty("background-color", color, "important")

							// Apply auto-calculated text color
							const isLight = isColorLight(color)
							const textColor = isLight ? "#000000" : "#ffffff"
							htmlChip.style.setProperty("color", textColor, "important")

							// Apply to text elements inside chip
							const textElement = chip.querySelector(".p-chip-text")
							if (textElement) {
								;(textElement as HTMLElement).style.setProperty("color", textColor, "important")
							}

							// Apply to icon elements
							const chipIcons = chip.querySelectorAll(".p-chip-remove-icon, .pi")
							chipIcons.forEach((icon: Element) => {
								;(icon as HTMLElement).style.setProperty("color", textColor, "important")
							})
						} else if (mode === "text") {
							htmlChip.style.color = color

							// Also apply to text elements inside each chip
							const textElement = chip.querySelector(".p-chip-text")
							if (textElement) {
								const htmlTextElement = textElement as HTMLElement
								htmlTextElement.style.color = color
							}

							// Apply icon color same as text color
							const chipIcons = chip.querySelectorAll(".p-chip-remove-icon, .pi")
							chipIcons.forEach((icon: Element) => {
								;(icon as HTMLElement).style.color = color
							})
						}
					})
				}
			}
		},

		"p-breadcrumb": () => {
			const breadcrumbConfig = initializeComponentsStructure(["breadcrumb", "colorScheme", "light"])
			if (!breadcrumbConfig.item) breadcrumbConfig.item = {}
			if (!breadcrumbConfig.item.icon) breadcrumbConfig.item.icon = {}

			breadcrumbConfig.item.color = color
			breadcrumbConfig.item.icon.color = color

			// Apply directly to the preview element only
			if (clickedElement) {
				clickedElement.style.color = color
			}
		},

		"p-card": () => {
			const cardConfig = initializeComponentsStructure(["card", "colorScheme", "light", "root"])

			if (mode === "background") {
				cardConfig.background = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.backgroundColor = color
				}
			} else if (mode === "text") {
				cardConfig.color = color

				// Apply directly to the preview element only
				if (clickedElement) {
					clickedElement.style.color = color
				}
			}
		},

		// Section A — Dialog Colors
		"dialog-title-bg": () => {
			const semanticConfig = initializeSemanticStructure(["dialog"])
			if (!semanticConfig.titleBackgroundColor) {
				semanticConfig.titleBackgroundColor = "#084c61"
			}
			semanticConfig.titleBackgroundColor = color
			document.documentElement.style.setProperty("--p-dialog-title-background", color)
		},

		"dialog-title-text": () => {
			const semanticConfig = initializeSemanticStructure(["dialog"])
			if (!semanticConfig.titleTextColor) {
				semanticConfig.titleTextColor = "#ffffff"
			}
			semanticConfig.titleTextColor = color
			document.documentElement.style.setProperty("--p-dialog-title-color", color)
		},

		// Section B — Form Field Colors
		"form-error": () => {
			const semanticConfig = initializeSemanticStructure(["formColors"])
			semanticConfig.errorColor = color
			document.documentElement.style.setProperty("--p-form-error-color", color)
		},

		"form-label": () => {
			const semanticConfig = initializeSemanticStructure(["formColors"])
			semanticConfig.labelColor = color
			document.documentElement.style.setProperty("--p-form-label-color", color)
		},

		"form-border": () => {
			const semanticConfig = initializeSemanticStructure(["formColors"])
			semanticConfig.borderColor = color
			document.documentElement.style.setProperty("--p-form-border-color", color)
		},

		"form-focus-border": () => {
			const semanticConfig = initializeSemanticStructure(["formColors"])
			semanticConfig.focusBorderColor = color
			document.documentElement.style.setProperty("--p-form-focus-border-color", color)
		},

		"form-warning-icon": () => {
			const semanticConfig = initializeSemanticStructure(["formColors"])
			semanticConfig.warningIconColor = color
			document.documentElement.style.setProperty("--p-form-warning-icon-color", color)
		},

		// Section C — DataTable Colors (header bg, header text, row hover)
		"datatable-header-bg": () => {
			const datatableConfig = initializeComponentsStructure(["datatable", "colorScheme", "light"])
			if (!datatableConfig.headerCell) datatableConfig.headerCell = {}
			datatableConfig.headerCell.background = color
			document.documentElement.style.setProperty("--p-datatable-header-cell-background", color)

			const allHeaderCells = document.querySelectorAll(".component-section .p-datatable thead th")
			allHeaderCells.forEach((cell: Element) => {
				;(cell as HTMLElement).style.setProperty("background-color", color, "important")
			})
		},

		"datatable-header-text": () => {
			const datatableConfig = initializeComponentsStructure(["datatable", "colorScheme", "light"])
			if (!datatableConfig.headerCell) datatableConfig.headerCell = {}
			datatableConfig.headerCell.color = color
			document.documentElement.style.setProperty("--p-datatable-header-cell-color", color)

			const allHeaderCells = document.querySelectorAll(".component-section .p-datatable thead th")
			allHeaderCells.forEach((cell: Element) => {
				;(cell as HTMLElement).style.setProperty("color", color, "important")
			})
		},

		"datatable-row-hover-bg": () => {
			const datatableConfig = initializeComponentsStructure(["datatable", "colorScheme", "light"])
			if (!datatableConfig.row) datatableConfig.row = {}
			datatableConfig.row.hoverBackground = color
			document.documentElement.style.setProperty("--p-datatable-row-hover-background", color)
		},

		// Section D — Toast Colors
		"toast-success-bg": () => {
			const toastRoot = initializeComponentsStructure(["toast", "colorScheme", "light", "root"])
			if (!toastRoot.success) toastRoot.success = {}
			const adaptedColor = isColorLight(color) ? calculateHoverColor(color, 0.8) : calculateHoverColor(color, -0.8)
			toastRoot.success.background = color
			toastRoot.success.activeBackground = color
			toastRoot.success.color = adaptedColor
			toastRoot.success.borderColor = adaptedColor

			const elements = document.querySelectorAll(".component-section .toast-preview.toast-success")
			elements.forEach((el: Element) => {
				;(el as HTMLElement).style.setProperty("background-color", color, "important")
				;(el as HTMLElement).style.setProperty("color", adaptedColor, "important")
				;(el as HTMLElement).style.setProperty("border-color", adaptedColor, "important")
			})
		},

		"toast-error-bg": () => {
			const toastRoot = initializeComponentsStructure(["toast", "colorScheme", "light", "root"])
			if (!toastRoot.error) toastRoot.error = {}
			const adaptedColor = isColorLight(color) ? calculateHoverColor(color, 0.8) : calculateHoverColor(color, -0.8)
			toastRoot.error.background = color
			toastRoot.error.activeBackground = color
			toastRoot.error.color = adaptedColor
			toastRoot.error.borderColor = adaptedColor

			const elements = document.querySelectorAll(".component-section .toast-preview.toast-error")
			elements.forEach((el: Element) => {
				;(el as HTMLElement).style.setProperty("background-color", color, "important")
				;(el as HTMLElement).style.setProperty("color", adaptedColor, "important")
				;(el as HTMLElement).style.setProperty("border-color", adaptedColor, "important")
			})
		},

		"toast-warning-bg": () => {
			const toastRoot = initializeComponentsStructure(["toast", "colorScheme", "light", "root"])
			if (!toastRoot.warning) toastRoot.warning = {}
			const adaptedColor = isColorLight(color) ? calculateHoverColor(color, 0.8) : calculateHoverColor(color, -0.8)
			toastRoot.warning.background = color
			toastRoot.warning.activeBackground = color
			toastRoot.warning.color = adaptedColor
			toastRoot.warning.borderColor = adaptedColor

			const elements = document.querySelectorAll(".component-section .toast-preview.toast-warn")
			elements.forEach((el: Element) => {
				;(el as HTMLElement).style.setProperty("background-color", color, "important")
				;(el as HTMLElement).style.setProperty("color", adaptedColor, "important")
				;(el as HTMLElement).style.setProperty("border-color", adaptedColor, "important")
			})
		},

		"toast-info-bg": () => {
			const toastRoot = initializeComponentsStructure(["toast", "colorScheme", "light", "root"])
			if (!toastRoot.info) toastRoot.info = {}
			const adaptedColor = isColorLight(color) ? calculateHoverColor(color, 0.8) : calculateHoverColor(color, -0.8)
			toastRoot.info.background = color
			toastRoot.info.activeBackground = color
			toastRoot.info.color = adaptedColor
			toastRoot.info.borderColor = adaptedColor

			const elements = document.querySelectorAll(".component-section .toast-preview.toast-info")
			elements.forEach((el: Element) => {
				;(el as HTMLElement).style.setProperty("background-color", color, "important")
				;(el as HTMLElement).style.setProperty("color", adaptedColor, "important")
				;(el as HTMLElement).style.setProperty("border-color", adaptedColor, "important")
			})
		},

		"toast-preview": () => {
			if (!clickedElement) return

			// Determine the toast severity from clicked element or its parents
			let severity = "success" // default
			const toastElement = clickedElement.closest(".toast-preview")
			if (toastElement) {
				if (toastElement.classList.contains("toast-info")) {
					severity = "info"
				} else if (toastElement.classList.contains("toast-success")) {
					severity = "success"
				} else if (toastElement.classList.contains("toast-warn")) {
					severity = "warning"
				} else if (toastElement.classList.contains("toast-error")) {
					severity = "error"
				}
			}

			// Initialize toast structure if not present using plain objects
			const themeTemplate = localThemeState.value.selectedTheme
			if (!themeTemplate?.components) {
				themeTemplate.components = {}
			}
			if (!themeTemplate.components.toast) {
				themeTemplate.components.toast = {
					colorScheme: {
						light: {
							root: {
								success: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
								error: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
								warning: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
								info: { background: "", color: "", borderColor: "", activeBackground: "", activeColor: "" },
							},
						},
					},
				}
			}

			const toastConfig = themeTemplate.components.toast.colorScheme.light.root

			// Same logic as messages: text and border should be at opposite ends of the color scale for maximum contrast (80%)
			const adaptedColor = isColorLight(color)
				? calculateHoverColor(color, 0.8) // Make much darker (80%) if background is light
				: calculateHoverColor(color, -0.8) // Make much lighter (80%) if background is dark

			;(toastConfig as any)[severity].background = color
			;(toastConfig as any)[severity].activeBackground = color
			;(toastConfig as any)[severity].color = adaptedColor
			;(toastConfig as any)[severity].activeColor = adaptedColor
			;(toastConfig as any)[severity].borderColor = adaptedColor

			// Apply directly to the clicked toast preview
			if (clickedElement) {
				clickedElement.style.backgroundColor = color
				clickedElement.style.color = adaptedColor
				clickedElement.style.borderColor = adaptedColor

				// Apply the same color to icons within this toast
				const toastIcon = clickedElement.querySelector(".toast-icon i")
				if (toastIcon) {
					;(toastIcon as HTMLElement).style.setProperty("color", adaptedColor, "important")
				}

				const toastClose = clickedElement.querySelector(".toast-close i")
				if (toastClose) {
					;(toastClose as HTMLElement).style.setProperty("color", adaptedColor, "important")
				}
			}
		},

		"p-toast": () => {
			// Handle different toast types based on context
			const toastConfig = initializeComponentsStructure(["toast", "colorScheme", "light", "root"])

			// Same logic as messages: text should be at opposite ends of the color scale for maximum contrast (80%)
			const adaptedColor = isColorLight(color)
				? calculateHoverColor(color, 0.8) // Make much darker (80%) if background is light
				: calculateHoverColor(color, -0.8) // Make much lighter (80%) if background is dark

			// Default to success type, but this could be enhanced to detect toast type
			if (!toastConfig.success) toastConfig.success = {}
			toastConfig.success.background = color
			toastConfig.success.activeBackground = color
			toastConfig.success.color = adaptedColor
			toastConfig.success.activeColor = adaptedColor

			// Apply directly to the preview element only
			if (clickedElement) {
				clickedElement.style.backgroundColor = color
				clickedElement.style.color = adaptedColor
			}
		},

		// Downstream Distribution Table components
		"downstream-distribution": () => {
			if (!clickedElement) return

			const table = clickedElement.closest(".data-table")
			if (!table) return

			const semanticConfig = initializeSemanticStructure(["downstreamDistributionTable"])

			// Initialize with the provided semantic values
			if (!semanticConfig.stepField) {
				semanticConfig.stepField = {
					backgroundColor: "#809bce",
					textColor: "#000",
				}
			}
			if (!semanticConfig.transportGroup) {
				semanticConfig.transportGroup = {
					backgroundColor: "#95b8d1",
					textColor: "#000",
				}
			}
			if (!semanticConfig.wharehousingGroup) {
				semanticConfig.wharehousingGroup = {
					backgroundColor: "#b8e0d4",
					textColor: "#000",
				}
			}
			if (!semanticConfig.actionField) {
				semanticConfig.actionField = {
					backgroundColor: "#d6eadf",
					textColor: "#000",
				}
			}
			if (!semanticConfig.stepRow) {
				semanticConfig.stepRow = {
					backgroundColor: "#f8f9fa",
					textColor: "#000",
				}
			}

			// Find the actual TH cell that contains our custom classes
			const thCell = clickedElement.closest("th") as HTMLElement
			if (thCell) {
				// TH cell logic - only execute when we have a TH cell
				if (thCell) {
					// Check if we're clicking on an empty cell in the first row that should affect Step or Actions
					const currentRow = thCell.closest("tr")
					const isFirstRow = currentRow === table.querySelector("thead tr:first-child")

					// Handle clicks on first row empty cells (should affect corresponding cells in second row)
					if (isFirstRow && currentRow) {
						const columnIndex = Array.from(currentRow.children).indexOf(thCell)

						// First column (index 0) corresponds to Step in second row
						if (columnIndex === 0) {
							if (mode === "background") {
								semanticConfig.stepField.backgroundColor = color

								// Apply to clicked cell
								thCell.style.setProperty("background-color", color, "important")
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("background-color", color, "important")
								}
							} else if (mode === "text") {
								semanticConfig.stepField.textColor = color

								// Apply only to content inside the cell, not the cell itself
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}

								// Also apply to any other text elements in the cell
								const allTextElements = thCell.querySelectorAll("*")
								allTextElements.forEach((el: Element) => {
									const htmlEl = el as HTMLElement
									if (htmlEl !== content) {
										htmlEl.style.setProperty("color", color, "important")
									}
								})
							}

							// Find and color the Step cell in second row
							const secondRow = table.querySelector("thead tr:nth-child(2)")
							if (secondRow) {
								const stepCell = secondRow.querySelector(".step-field-header") as HTMLElement
								if (stepCell) {
									if (mode === "background") {
										stepCell.style.setProperty("background-color", color, "important")
										const stepContent = stepCell.querySelector(".p-datatable-column-header-content")
										if (stepContent) {
											const htmlStepContent = stepContent as HTMLElement
											htmlStepContent.style.setProperty("background-color", color, "important")
										}
									} else if (mode === "text") {
										// Apply only to content inside the cell, not the cell itself
										const stepContent = stepCell.querySelector(".p-datatable-column-header-content")
										if (stepContent) {
											const htmlStepContent = stepContent as HTMLElement
											htmlStepContent.style.setProperty("color", color, "important")
										}

										// Also apply to any other text elements in the cell
										const allTextElements = stepCell.querySelectorAll("*")
										allTextElements.forEach((el: Element) => {
											const htmlEl = el as HTMLElement
											if (htmlEl !== stepContent) {
												htmlEl.style.setProperty("color", color, "important")
											}
										})
									}
								}
							}

							// Also set CSS variables for consistency
							const root = document.documentElement
							if (mode === "background") {
								root.style.setProperty("--p-downstream-distribution-table-step-field-background-color", color)
							} else if (mode === "text") {
								root.style.setProperty("--p-downstream-distribution-table-step-field-text-color", color)
							}

							// Process all button hover colors before saving
							processAllButtonHoverColors()

							// Emit theme update to save changes
							emit("updateTheme", localThemeState.value)

							return // Exit early since we handled this case

							// Last column corresponds to Actions in second row
						} else if (currentRow && columnIndex === currentRow.children.length - 1) {
							if (mode === "background") {
								semanticConfig.actionField.backgroundColor = color

								// Apply to clicked cell
								thCell.style.setProperty("background-color", color, "important")
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("background-color", color, "important")
								}
							} else if (mode === "text") {
								semanticConfig.actionField.textColor = color

								// Apply to clicked cell
								thCell.style.setProperty("color", color, "important")
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}
							}

							// Find and color the Actions cell in second row
							const secondRow = table.querySelector("thead tr:nth-child(2)")
							if (secondRow) {
								const actionCell = secondRow.querySelector(".action-field-header") as HTMLElement
								if (actionCell) {
									if (mode === "background") {
										actionCell.style.setProperty("background-color", color, "important")
										const actionContent = actionCell.querySelector(".p-datatable-column-header-content")
										if (actionContent) {
											const htmlActionContent = actionContent as HTMLElement
											htmlActionContent.style.setProperty("background-color", color, "important")
										}
									} else if (mode === "text") {
										// Apply only to content inside the cell, not the cell itself
										const actionContent = actionCell.querySelector(".p-datatable-column-header-content")
										if (actionContent) {
											const htmlActionContent = actionContent as HTMLElement
											htmlActionContent.style.setProperty("color", color, "important")
										}

										// Also apply to any other text elements in the cell
										const allTextElements = actionCell.querySelectorAll("*")
										allTextElements.forEach((el: Element) => {
											const htmlEl = el as HTMLElement
											if (htmlEl !== actionContent) {
												htmlEl.style.setProperty("color", color, "important")
											}
										})
									}
								}
							}

							// Also set CSS variables for consistency
							const root = document.documentElement
							if (mode === "background") {
								root.style.setProperty("--p-downstream-distribution-table-action-field-background-color", color)
							} else if (mode === "text") {
								root.style.setProperty("--p-downstream-distribution-table-action-field-text-color", color)
							}

							// Process all button hover colors before saving
							processAllButtonHoverColors()

							// Emit theme update to save changes
							emit("updateTheme", localThemeState.value)

							return // Exit early since we handled this case
						}
					}

					// Handle clicks on second row cells or transport/warehousing groups
					if (thCell.classList.contains("step-field-header")) {
						if (mode === "background") {
							// Step: change only the step column (first column in both rows) background
							semanticConfig.stepField.backgroundColor = color

							// Apply to the TH cell directly (we already have it)
							thCell.style.setProperty("background-color", color, "important")

							// Also apply to any content inside the cell
							const content = thCell.querySelector(".p-datatable-column-header-content")
							if (content) {
								const htmlContent = content as HTMLElement
								htmlContent.style.setProperty("background-color", color, "important")
							}
						} else if (mode === "text") {
							// Step: change only the step column (first column in both rows) text color
							semanticConfig.stepField.textColor = color

							// Apply to the TH cell directly (we already have it)
							thCell.style.setProperty("color", color, "important")

							// Also apply to any content inside the cell
							const content = thCell.querySelector(".p-datatable-column-header-content")
							if (content) {
								const htmlContent = content as HTMLElement
								htmlContent.style.setProperty("color", color, "important")
							}
						}

						// Find the corresponding cell in the first row (index 0)
						const firstRow = table.querySelector("thead tr:first-child")

						if (firstRow) {
							const firstRowFirstCell = firstRow.children[0] as HTMLElement

							if (firstRowFirstCell) {
								if (mode === "background") {
									firstRowFirstCell.style.setProperty("background-color", color, "important")
									// Also apply to content inside first row cell
									const firstRowContent = firstRowFirstCell.querySelector(".p-datatable-column-header-content")
									if (firstRowContent) {
										const htmlFirstRowContent = firstRowContent as HTMLElement
										htmlFirstRowContent.style.setProperty("background-color", color, "important")
									}
								} else if (mode === "text") {
									firstRowFirstCell.style.setProperty("color", color, "important")
									// Also apply to content inside first row cell
									const firstRowContent = firstRowFirstCell.querySelector(".p-datatable-column-header-content")
									if (firstRowContent) {
										const htmlFirstRowContent = firstRowContent as HTMLElement
										htmlFirstRowContent.style.setProperty("color", color, "important")
									}
								}
							}
						}

						// Also set CSS variables for consistency
						const root = document.documentElement
						if (mode === "background") {
							root.style.setProperty("--p-downstream-distribution-table-step-field-background-color", color)
						} else if (mode === "text") {
							root.style.setProperty("--p-downstream-distribution-table-step-field-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					} else if (thCell.classList.contains("action-field-header")) {
						if (mode === "background") {
							// Actions: change only the actions column (last column in both rows) background
							semanticConfig.actionField.backgroundColor = color

							// Apply to the TH cell directly (we already have it)
							thCell.style.setProperty("background-color", color, "important")

							// Also apply to any content inside the cell
							const content = thCell.querySelector(".p-datatable-column-header-content")
							if (content) {
								const htmlContent = content as HTMLElement
								htmlContent.style.setProperty("background-color", color, "important")
							}
						} else if (mode === "text") {
							// Actions: change only the actions column (last column in both rows) text color
							semanticConfig.actionField.textColor = color

							// Apply only to content inside the cell, not the cell itself
							const content = thCell.querySelector(".p-datatable-column-header-content")
							if (content) {
								const htmlContent = content as HTMLElement
								htmlContent.style.setProperty("color", color, "important")
							}

							// Also apply to any other text elements in the cell
							const allTextElements = thCell.querySelectorAll("*")
							allTextElements.forEach((el: Element) => {
								const htmlEl = el as HTMLElement
								if (htmlEl !== content) {
									htmlEl.style.setProperty("color", color, "important")
								}
							})
						}

						// Find the corresponding cell in the first row (last cell)
						const firstRow = table.querySelector("thead tr:first-child")

						if (firstRow) {
							const firstRowLastCell = firstRow.children[firstRow.children.length - 1] as HTMLElement

							if (firstRowLastCell) {
								if (mode === "background") {
									firstRowLastCell.style.setProperty("background-color", color, "important")
									// Also apply to content inside first row last cell
									const firstRowLastContent = firstRowLastCell.querySelector(".p-datatable-column-header-content")
									if (firstRowLastContent) {
										const htmlFirstRowLastContent = firstRowLastContent as HTMLElement
										htmlFirstRowLastContent.style.setProperty("background-color", color, "important")
									}
								} else if (mode === "text") {
									// Apply only to content inside the cell, not the cell itself
									const firstRowLastContent = firstRowLastCell.querySelector(".p-datatable-column-header-content")
									if (firstRowLastContent) {
										const htmlFirstRowLastContent = firstRowLastContent as HTMLElement
										htmlFirstRowLastContent.style.setProperty("color", color, "important")
									}

									// Also apply to any other text elements in the cell
									const allTextElements = firstRowLastCell.querySelectorAll("*")
									allTextElements.forEach((el: Element) => {
										const htmlEl = el as HTMLElement
										if (htmlEl !== firstRowLastContent) {
											htmlEl.style.setProperty("color", color, "important")
										}
									})
								}
							}
						}

						// Also set CSS variables for consistency
						const root = document.documentElement
						if (mode === "background") {
							root.style.setProperty("--p-downstream-distribution-table-action-field-background-color", color)
						} else if (mode === "text") {
							root.style.setProperty("--p-downstream-distribution-table-action-field-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					} else if (thCell.classList.contains("transport-group-header")) {
						if (mode === "background") {
							// Transport header: change all transport related cells background
							semanticConfig.transportGroup.backgroundColor = color

							const transportCells = table.querySelectorAll(".transport-group-header, .transport-group-subheader")
							transportCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)
								thCell.style.setProperty("background-color", color, "important")
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-transport-group-background-color", color)
						} else if (mode === "text") {
							// Transport header: change all transport related cells text color
							semanticConfig.transportGroup.textColor = color

							const transportCells = table.querySelectorAll(".transport-group-header, .transport-group-subheader")
							transportCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)

								// Apply only to content inside the cell, not the cell itself
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}

								// Also apply to any other text elements in the cell
								const allTextElements = thCell.querySelectorAll("*")
								allTextElements.forEach((el: Element) => {
									const htmlEl = el as HTMLElement
									if (htmlEl !== content) {
										htmlEl.style.setProperty("color", color, "important")
									}
								})
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-transport-group-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					} else if (thCell.classList.contains("transport-group-subheader")) {
						if (mode === "background") {
							// Transport subheader: change all transport related cells background
							semanticConfig.transportGroup.backgroundColor = color

							const transportCells = table.querySelectorAll(".transport-group-header, .transport-group-subheader")
							transportCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)
								thCell.style.setProperty("background-color", color, "important")
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-transport-group-background-color", color)
						} else if (mode === "text") {
							// Transport subheader: change all transport related cells text color
							semanticConfig.transportGroup.textColor = color

							const transportCells = table.querySelectorAll(".transport-group-header, .transport-group-subheader")
							transportCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)

								// Apply only to content inside the cell, not the cell itself
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}

								// Also apply to any other text elements in the cell
								const allTextElements = thCell.querySelectorAll("*")
								allTextElements.forEach((el: Element) => {
									const htmlEl = el as HTMLElement
									if (htmlEl !== content) {
										htmlEl.style.setProperty("color", color, "important")
									}
								})
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-transport-group-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					} else if (thCell.classList.contains("warehousing-group-header")) {
						if (mode === "background") {
							// Warehousing header: change all warehousing related cells background
							semanticConfig.wharehousingGroup.backgroundColor = color

							const warehousingCells = table.querySelectorAll(".warehousing-group-header, .warehousing-group-subheader")
							warehousingCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)
								thCell.style.setProperty("background-color", color, "important")
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-wharehousing-group-background-color", color)
						} else if (mode === "text") {
							// Warehousing header: change all warehousing related cells text color
							semanticConfig.wharehousingGroup.textColor = color

							const warehousingCells = table.querySelectorAll(".warehousing-group-header, .warehousing-group-subheader")
							warehousingCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)

								// Apply only to content inside the cell, not the cell itself
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}

								// Also apply to any other text elements in the cell
								const allTextElements = thCell.querySelectorAll("*")
								allTextElements.forEach((el: Element) => {
									const htmlEl = el as HTMLElement
									if (htmlEl !== content) {
										htmlEl.style.setProperty("color", color, "important")
									}
								})
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-wharehousing-group-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					} else if (thCell.classList.contains("warehousing-group-subheader")) {
						if (mode === "background") {
							// Warehousing subheader: change all warehousing related cells background
							semanticConfig.wharehousingGroup.backgroundColor = color

							const warehousingCells = table.querySelectorAll(".warehousing-group-header, .warehousing-group-subheader")
							warehousingCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)
								thCell.style.setProperty("background-color", color, "important")
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-wharehousing-group-background-color", color)
						} else if (mode === "text") {
							// Warehousing subheader: change all warehousing related cells text color
							semanticConfig.wharehousingGroup.textColor = color

							const warehousingCells = table.querySelectorAll(".warehousing-group-header, .warehousing-group-subheader")
							warehousingCells.forEach((cell: Element) => {
								const thCell = ((cell as HTMLElement).closest("th") as HTMLElement) || (cell as HTMLElement)

								// Apply only to content inside the cell, not the cell itself
								const content = thCell.querySelector(".p-datatable-column-header-content")
								if (content) {
									const htmlContent = content as HTMLElement
									htmlContent.style.setProperty("color", color, "important")
								}

								// Also apply to any other text elements in the cell
								const allTextElements = thCell.querySelectorAll("*")
								allTextElements.forEach((el: Element) => {
									const htmlEl = el as HTMLElement
									if (htmlEl !== content) {
										htmlEl.style.setProperty("color", color, "important")
									}
								})
							})

							// Also set CSS variables for consistency
							const root = document.documentElement
							root.style.setProperty("--p-downstream-distribution-table-wharehousing-group-text-color", color)
						}

						// Process all button hover colors before saving
						processAllButtonHoverColors()

						// Emit theme update to save changes
						emit("updateTheme", localThemeState.value)
					}
				} else {
					// Check if we're clicking on a table row in tbody
					const tableRow = clickedElement.closest("tbody tr") as HTMLElement
					if (tableRow) {
						// stepRow semantic config is already initialized above

						// Check if this is a Step row (rows containing Step information)
						// Look for both text content and check if this is a title row with Step info
						const stepCell = tableRow.querySelector("td:first-child")
						const hasStepInText = stepCell && stepCell.textContent && stepCell.textContent.includes("Step")

						// Also check if this row has Step content in any cell (for preview table)
						const allCells = tableRow.querySelectorAll("td")
						let hasStepInAnyCell = false
						allCells.forEach((cell, index) => {
							const cellText = cell.textContent || ""
							const cellHTML = cell.innerHTML || ""
							if (cellText.includes("Step") || cellHTML.includes("Step")) {
								hasStepInAnyCell = true
							}
						})

						const isStepRow = hasStepInText || hasStepInAnyCell

						if (isStepRow) {
							if (mode === "background") {
								semanticConfig.stepRow.backgroundColor = color

								// Apply only to Step rows (rows with "Step" in any cell)
								const allRows = table.querySelectorAll("tbody tr")
								allRows.forEach((row: Element, index: number) => {
									const htmlRow = row as HTMLElement

									// Check all cells in this row for Step content
									const allRowCells = htmlRow.querySelectorAll("td")
									let isStepRowInLoop = false

									allRowCells.forEach((cell, cellIndex) => {
										const cellText = cell.textContent || ""
										const cellHTML = cell.innerHTML || ""
										if (cellText.includes("Step") || cellHTML.includes("Step")) {
											isStepRowInLoop = true
										}
									})

									if (isStepRowInLoop) {
										// Apply to the entire row element
										htmlRow.style.setProperty("background-color", color, "important")
										// Also apply to all cells to ensure complete coverage
										allRowCells.forEach((cell) => {
											const htmlCell = cell as HTMLElement
											htmlCell.style.setProperty("background-color", color, "important")

											// Apply background color to ALL child elements within the cell
											const childElements = htmlCell.querySelectorAll("*")
											childElements.forEach((child) => {
												;(child as HTMLElement).style.setProperty("background-color", color, "important")
											})
										})
									}
								})

								// Also set CSS variable
								const root = document.documentElement
								root.style.setProperty("--p-downstream-distribution-table-rows-background-color", color)
							} else if (mode === "text") {
								semanticConfig.stepRow.textColor = color

								// Apply only to Step rows (rows with "Step" in any cell)
								const allRows = table.querySelectorAll("tbody tr")
								allRows.forEach((row: Element, index: number) => {
									const htmlRow = row as HTMLElement

									// Check all cells in this row for Step content
									const allRowCells = htmlRow.querySelectorAll("td")
									let isStepRowInLoop = false

									allRowCells.forEach((cell, cellIndex) => {
										const cellText = cell.textContent || ""
										const cellHTML = cell.innerHTML || ""
										if (cellText.includes("Step") || cellHTML.includes("Step")) {
											isStepRowInLoop = true
										}
									})

									if (isStepRowInLoop) {
										// Apply to the entire row element
										htmlRow.style.setProperty("color", color, "important")
										// Also apply to all cells to ensure complete coverage
										allRowCells.forEach((cell) => {
											const htmlCell = cell as HTMLElement
											htmlCell.style.setProperty("color", color, "important")

											// Apply text color to ALL child elements within the cell
											const childElements = htmlCell.querySelectorAll("*")
											childElements.forEach((child) => {
												;(child as HTMLElement).style.setProperty("color", color, "important")
											})
										})
									}
								})

								// Also set CSS variable
								const root = document.documentElement
								root.style.setProperty("--p-downstream-distribution-table-rows-text-color", color)
							}

							// Emit theme update to save changes

							emit("updateTheme", localThemeState.value)
						} else {
							if (mode === "background") {
								tableRow.style.setProperty("background-color", color, "important")
							} else if (mode === "text") {
								tableRow.style.setProperty("color", color, "important")
							}
						}
					}
				}
			}
		},

		// Card Header component
		"card-header": () => {
			const semanticConfig = initializeSemanticStructure(["semantic", "cardHeader"])
			semanticConfig.backgroundColor = color
			semanticConfig.textColor = isColorLight(color) ? "#000000" : "#ffffff"

			// Apply directly to the preview element only
			if (clickedElement) {
				clickedElement.style.backgroundColor = color
				clickedElement.style.color = isColorLight(color) ? "#000000" : "#ffffff"
			}
		},

		// Landing Page Utilities Buttons
		"landingpage-utilities-button": () => {
			const semanticConfig = initializeSemanticStructure(["landingpageUtilitiesButtons"])

			// When user chooses a color, set inverted hover effect:
			// Normal state: transparent background, colored text/border
			// Hover state: colored background, white text/border
			semanticConfig.backgroundColor = "transparent"
			semanticConfig.textColor = color
			semanticConfig.backgroundColorHover = color
			semanticConfig.textColorHover = "#ffffff"

			// Set CSS variables
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-background-color", "transparent")
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color", color)
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-background-color-hover", color)
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color-hover", "#ffffff")

			// Process all button hover colors before saving
			processAllButtonHoverColors()

			// Emit theme update to save changes
			emit("updateTheme", localThemeState.value)
		},
	}

	// Find matching class and apply color (enhanced matching)
	let match: string | undefined

	// Special case: if we're inside downstream distribution table, force downstream matching (check this FIRST!)
	if (clickedElement?.closest(".downstream-distribution-preview")) {
		match = "downstream-distribution"
	} else if (clickedElement?.closest(".p-datatable")) {
		// Only use regular datatable if not in downstream distribution
		match = "p-datatable"
	} else if (clickedElement?.classList.contains("landingpage-utilities-button") || clickedElement?.closest("[data-component-type='landingpage-utilities-button']")) {
		// Special case for landing page utilities buttons
		match = "landingpage-utilities-button"
	} else {
		// First try exact class matches
		match = Object.keys(colorMappings).find((key) => classes.some((cls) => cls === key))

		// If no exact match, try partial matches
		if (!match) {
			match = Object.keys(colorMappings).find((key) => classes.some((cls) => cls.includes(key.replace("p-", ""))))
		}

		// If still no match, try component type detection
		if (!match) {
			match = Object.keys(colorMappings).find((key) => classes.some((cls) => cls.startsWith(key) || cls.includes(key.substring(2))))
		}
	}

	if (match) {
		const updateFunction = colorMappings[match]
		if (updateFunction) {
			updateFunction()
			// Process all button hover colors before saving
			processAllButtonHoverColors()
			emit("updateTheme", localThemeState.value)
		} else {
		}
	}
}

const onHexInputChange = () => {
	let value = hexInputValue.value.replace(/[^#0-9A-Fa-f]/g, "")
	if (value && !value.startsWith("#")) {
		value = "#" + value
	}
	if (value.length > 7) {
		value = value.substring(0, 7)
	}
	if (hexInputValue.value !== value) {
		hexInputValue.value = value
	}
}

const validateAndApplyHex = () => {
	if (isValidHexColor(hexInputValue.value)) {
		if (colorMode.value === "text") {
			selectedTextColor.value = hexInputValue.value
		} else {
			selectedColor.value = hexInputValue.value

			// Auto-contrast: automatically set optimal text color if enabled
			if (autoContrast.value && clickedElement) {
				const optimalTextColor = getOptimalTextColor(hexInputValue.value)

				// Save current mode and switch to text mode temporarily
				const originalMode = colorMode.value
				colorMode.value = "text"

				// Set optimal text color
				selectedTextColor.value = optimalTextColor
				hexInputValue.value = optimalTextColor

				// Apply the color to the current component (as if user clicked on text)
				applyColor()

				// Restore original mode
				colorMode.value = originalMode
			}
		}
		applyColor()
	} else {
		// Reset to current color based on mode
		const currentColor = colorMode.value === "text" ? selectedTextColor.value : selectedColor.value
		hexInputValue.value = currentColor
	}
}

const isValidHexColor = (hex: string): boolean => {
	return /^#[0-9A-Fa-f]{6}$/.test(hex)
}

const sendColorToParent = () => {
	const currentColor = colorMode.value === "text" ? selectedTextColor.value : selectedColor.value
	if (isValidHexColor(currentColor)) {
		emit("colorSelected", currentColor)
		// Close the color picker after sending color to parent
		showPicker.value = false
	}
}

const rgbToHex = (rgb: string) => {
	if (rgb.startsWith("#")) {
		return rgb.length === 7 ? rgb : "#ffffff"
	}

	const result = rgb.match(/\d+/g)
	if (!result || result.length < 3) {
		return "#ffffff"
	}

	return (
		"#" +
		result
			.slice(0, 3)
			.map((x) => {
				const num = parseInt(x)
				return num.toString(16).padStart(2, "0")
			})
			.join("")
	)
}

// Watch for template changes (following login page pattern)
watch(
	() => props.selectedTemplate,
	(newTemplate) => {
		if (newTemplate?.selectedTheme !== undefined) {
			applyTemplate(newTemplate)
		}
	},
	{ immediate: true }
)

// Expose functions for parent components
defineExpose({
	getCurrentFontSettings,
	loadFontSettingsFromTheme,
})
</script>

<style lang="scss" scoped>
.full-width {
	width: 100%;
	box-sizing: border-box;
}

.components-container {
	max-width: 900px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 0;
	flex: 1;
}

// Text Formatting Panel Styles
.text-formatting-panel {
	width: 280px;
	background: #f8f9fa;
	border-radius: 8px;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 16px;
	height: fit-content;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	position: sticky;
	top: 150px;
	align-self: flex-start;
}

.text-formatting-title {
	font-size: 16px;
	font-weight: 600;
	color: #1f2937;
	margin: 0 0 8px 0;
	padding-bottom: 8px;
	border-bottom: 2px solid #e5e7eb;
}

.formatting-group {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.formatting-label {
	font-size: 13px;
	font-weight: 500;
	color: #4b5563;
}

.color-preview {
	width: 48px;
	height: 32px;
	border-radius: 4px;
	border: 1px solid #d1d5db;
	cursor: pointer;
	transition: all 0.2s;
	flex-shrink: 0;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		transform: scale(1.05);
	}
}

.color-input-field {
	flex: 1;
	max-width: calc(100% - 58px);
}

.text-style-options {
	display: flex;
	gap: 8px;
}

.text-style-btn {
	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;

	&.bold-btn {
		font-weight: 700;
	}

	&.italic-btn {
		font-style: italic;
	}

	&.underline-btn {
		text-decoration: underline;
	}
}

.apply-text-btn {
	width: 100% !important;
	margin-top: 8px;
	font-weight: 600;
	transition: all 0.3s ease;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}
}

.text-preview-container {
	background: white;
	border: 2px solid #e5e7eb;
	border-radius: 6px;
	padding: 16px;
	margin-bottom: 8px;
}

.text-formatting-panel .text-preview-container .text-preview {
	text-align: center;
	padding: 8px;
	transition: all 0.3s ease;
	/* Higher specificity to override global font settings */
	/* Let inline styles apply by not setting font properties here */
}

.font-size-btn-group {
	display: flex;
	flex-direction: column;
	gap: 1px;
}

.font-size-btn {
	width: 28px !important;
	height: 20px !important;
	padding: 0 !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	min-width: unset !important;
	background-color: #64748b !important;
	border-color: #64748b !important;
	color: white !important;

	&:hover:not(:disabled) {
		background-color: #475569 !important;
		border-color: #475569 !important;
	}

	&:disabled {
		background-color: #cbd5e1 !important;
		border-color: #cbd5e1 !important;
		color: #94a3b8 !important;
	}

	&.font-size-up {
		border-bottom-left-radius: 0 !important;
		border-bottom-right-radius: 0 !important;
	}

	&.font-size-down {
		border-top-left-radius: 0 !important;
		border-top-right-radius: 0 !important;
	}

	.font-size-arrow {
		font-size: 12px !important;
		font-weight: bold !important;
		display: inline-block !important;
		line-height: 1 !important;
		color: white !important;
	}
}

// Color Picker Styles
.color-picker-panel {
	background: white;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	border: 1px solid #e0e0e0;
	min-width: 280px;
	overflow: hidden;
	padding: 16px;
}

.color-picker-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background: #f8f9fa;
	border-bottom: 1px solid #e0e0e0;
	margin: -16px -16px 16px -16px;
}

.color-picker-title {
	font-weight: 600;
	color: #333;
	font-size: 14px;
}

.color-picker-subtitle {
	padding: 8px 16px;
	background: #e8f4fd;
	border-bottom: 1px solid #bee3f8;
	font-size: 12px;
	font-weight: 500;
	color: #2b6cb0;
	text-align: center;
	margin: -16px -16px 12px -16px;
}

.color-picker-close {
	background: none;
	border: none;
	font-size: 20px;
	color: #666;
	cursor: pointer;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	transition: all 0.2s ease;
}

.color-picker-close:hover {
	background: #e9ecef;
	color: #333;
}

.mode-tabs {
	display: flex;
	margin: 0 -16px 16px -16px;
	background: #f8f9fa;
	border-bottom: 1px solid #e0e0e0;
}

.mode-tab {
	flex: 1;
	margin: 0;
	border-radius: 0;
	border: none;
	background: transparent;
	color: #666;
	font-weight: 500;
	padding: 4px 8px;
	font-size: 0.75rem;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	transition: all 0.2s ease;

	&.active {
		background: #007bff;
		color: white;
	}

	&:hover:not(.active) {
		background: #e9ecef;
		color: #333;
	}

	i {
		margin-right: 6px;
	}
}

.color-picker {
	border: none !important;
	box-shadow: none !important;
}

.color-picker-content {
	display: flex;
	gap: 16px;
	align-items: flex-start;
}

.color-palette-miniatures {
	flex-shrink: 0;
	width: 80px;
}

.palette-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.primary-colors-box {
	border: 2px solid #000000;
	border-radius: 4px;
	padding: 4px;
	margin-bottom: 6px;
}

.palette-row {
	display: flex;
	gap: 4px;
	justify-content: space-between;
}

.palette-swatch {
	width: 24px;
	height: 24px;
	border-radius: 4px;
	cursor: pointer;
	border: 1px solid #e0e0e0;
	transition: all 0.2s ease;
}

.palette-swatch:hover {
	transform: scale(1.1);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.primary-swatch {
	border: 2px solid #000000 !important;
	font-weight: bold;
}

.primary-row .palette-swatch {
	position: relative;
}

.hex-input-container {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	border-top: 1px solid #e0e0e0;
	background: #f8f9fa;
}

.hex-input-label {
	font-size: 12px;
	font-weight: 500;
	color: #666;
	min-width: 30px;
}

.hex-input {
	flex: 1;
	font-family: monospace;
	font-size: 12px;
	text-transform: uppercase;
}

.send-color-btn {
	min-width: 32px !important;
	height: 32px !important;
	padding: 0 !important;
	border-radius: 4px !important;
	background: #007bff !important;
	color: white !important;
	border: none !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.2s ease !important;
}

.send-color-btn:hover {
	background: #0056b3 !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Ensure chip icons have the same color as chip text */
:deep(.p-chip .p-chip-remove-icon) {
	color: inherit !important;
}

:deep(.p-chip .pi) {
	color: inherit !important;
}
</style>
